/**
 * BEATSEEK Producers Corner Component
 * Tips, tricks, news, demo drops, label connections for music producers
 * NOW WITH YOUTUBE VIDEO BOARD FEATURE
 */

import React, { useState, useEffect } from 'react';
import { X, ExternalLink, Music, Mic, Users, Zap, Star, Calendar, Play, Eye, ThumbsUp, Share2, Search, Filter, Grid, ChevronDown, Youtube, Edit3, Trash2, Smile } from 'lucide-react';
import EmotePicker from './EmotePicker';
import { renderEmotesInText } from '../utils/emoteRenderer';
import ReactionPicker from './ReactionPicker';
import Reactions from './Reactions';
import EmoteInput from './EmoteInput';
import PresenceDot from './PresenceDot';
import WhisperIcon from './WhisperIcon';
import LabelConnectTab from './ProducerCorner/LabelConnectTab';
import ConnectTab from './ProducerCorner/ConnectTab';
import DemoDropsTab from './ProducerCorner/DemoDropsTab';

// Audio Preview Player Component
const AudioPreviewPlayer = ({ audioId, postId }) => {
  const [audioInfo, setAudioInfo] = useState(null);
  const [audioLoading, setAudioLoading] = useState(true);
  const [audioError, setAudioError] = useState(null);
  const [audioPlayer, setAudioPlayer] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (audioId) {
      fetchAudioInfo();
    }
  }, [audioId]);

  const fetchAudioInfo = async () => {
    try {
      setAudioLoading(true);
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${BACKEND_URL}/api/forum/audio/${audioId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAudioInfo(data);
        setAudioError(null);
      } else if (response.status === 404 || response.status === 410) {
        // Audio expired or not found
        setAudioError('expired');
      } else {
        setAudioError('failed');
      }
    } catch (error) {
      console.error('Error fetching audio info:', error);
      setAudioError('failed');
    } finally {
      setAudioLoading(false);
    }
  };

  const formatTimeRemaining = (daysRemaining) => {
    if (daysRemaining <= 0) return 'Expired';
    if (daysRemaining === 1) return '1 day left';
    return `${daysRemaining} days left`;
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const togglePlayback = () => {
    if (audioPlayer) {
      if (isPlaying) {
        audioPlayer.pause();
      } else {
        audioPlayer.play();
      }
    }
  };

  if (audioLoading) {
    return (
      <div className="bg-gray-800/50 border border-gray-700/50 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-500"></div>
          <span className="text-gray-300">Loading track preview...</span>
        </div>
      </div>
    );
  }

  if (audioError === 'expired') {
    return (
      <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-3">
          <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-red-300 font-semibold">Track preview expired</span>
          <span className="text-red-200 text-sm">- feedback period ended</span>
        </div>
      </div>
    );
  }

  if (audioError === 'failed') {
    return (
      <div className="bg-gray-800/50 border border-gray-700/50 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-3">
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-gray-400">Track preview unavailable</span>
        </div>
      </div>
    );
  }

  if (!audioInfo) return null;

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const audioUrl = `${BACKEND_URL}/api/forum/audio/${audioId}/play`;
  const timeStatus = audioInfo.days_remaining > 0 ? 
    `🎵 Track Preview - ${formatTimeRemaining(audioInfo.days_remaining)} for feedback` :
    'Track preview expired';

  return (
    <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-4 mb-4">
      {/* Header with time status */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
          </svg>
          <span className="text-green-300 font-semibold text-sm">{timeStatus}</span>
        </div>
        <span className="text-green-200 text-xs">{audioInfo.original_filename}</span>
      </div>

      {/* Audio Player */}
      <div className="bg-gray-800/50 rounded-lg p-3">
        <audio
          ref={setAudioPlayer}
          src={audioUrl}
          onLoadedMetadata={(e) => {
            setDuration(e.target.duration);
          }}
          onTimeUpdate={(e) => {
            setCurrentTime(e.target.currentTime);
          }}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => setIsPlaying(false)}
          preload="metadata"
        />
        
        <div className="flex items-center gap-3">
          {/* Play/Pause Button */}
          <button
            onClick={togglePlayback}
            className="flex items-center justify-center w-10 h-10 bg-green-600 hover:bg-green-700 text-white rounded-full transition-colors duration-300"
          >
            {isPlaying ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : (
              <svg className="w-5 h-5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h1m4 0h1m-6-8h1m4 0h1M7 7h10a2 2 0 012 2v6a2 2 0 01-2 2H7a2 2 0 01-2-2V9a2 2 0 012-2z" />
              </svg>
            )}
          </button>

          {/* Progress Bar */}
          <div className="flex-1">
            <div 
              className="w-full bg-gray-700 rounded-full h-2 cursor-pointer"
              onClick={(e) => {
                if (audioPlayer && duration) {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const percent = (e.clientX - rect.left) / rect.width;
                  const newTime = percent * duration;
                  audioPlayer.currentTime = newTime;
                  setCurrentTime(newTime);
                }
              }}
            >
              <div 
                className="bg-green-500 h-2 rounded-full transition-all duration-100 relative" 
                style={{width: duration ? `${(currentTime / duration) * 100}%` : '0%'}}
              >
                {/* Progress handle */}
                <div className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full border-2 border-green-500 opacity-0 hover:opacity-100 transition-opacity duration-200" />
              </div>
            </div>
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>{formatDuration(currentTime)}</span>
              <span>{formatDuration(duration)}</span>
            </div>
          </div>

          {/* Enhanced Controls */}
          <div className="flex items-center gap-2">
            {/* Loop Button */}
            <button
              onClick={() => {
                if (audioPlayer) {
                  audioPlayer.loop = !audioPlayer.loop;
                }
              }}
              className="p-1 rounded hover:bg-gray-600 transition-colors duration-200"
              title="Loop"
            >
              <svg className="w-4 h-4 text-gray-400 hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            
            {/* Volume */}
            <div className="flex items-center gap-1">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 14.142M9 9a3 3 0 000 6h4a1 1 0 001-1v-4a1 1 0 00-1-1H9z" />
              </svg>
              <span className="text-xs text-gray-400">45s clip</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProducersCorner = ({ isOpen, onClose, user, onWhisperClick, initialCategory = 'videos' }) => {
  const [selectedCategory, setSelectedCategory] = useState(initialCategory); // 'videos', 'forum', 'tips', 'demos', 'labels', 'news'
  
  // Reset to initial category when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setSelectedCategory(initialCategory);
    }
  }, [isOpen, initialCategory]);
  
  // Video board state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVideoCategory, setSelectedVideoCategory] = useState('all');
  const [sortBy, setSortBy] = useState('trending');
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);

  // Forum state
  const [selectedForum, setSelectedForum] = useState(null); // null = main forum page, 'qa', 'stories', 'feedback'
  const [forumPosts, setForumPosts] = useState([]);
  const [forumLoading, setForumLoading] = useState(false);
  const [newPostContent, setNewPostContent] = useState('');
  const [newPostTitle, setNewPostTitle] = useState('');
  const [showNewPostModal, setShowNewPostModal] = useState(false);
  const [submittingPost, setSubmittingPost] = useState(false);

  // Audio Track Preview state
  const [showAudioWarning, setShowAudioWarning] = useState(false);
  const [audioFile, setAudioFile] = useState(null);
  const [audioPreviewUrl, setAudioPreviewUrl] = useState(null);
  const [audioPlayer, setAudioPlayer] = useState(null);
  const [audioUploading, setAudioUploading] = useState(false);
  const [audioUploadId, setAudioUploadId] = useState(null);
  const [clipStartTime, setClipStartTime] = useState(0);
  const [clipEndTime, setClipEndTime] = useState(45);
  const [audioDuration, setAudioDuration] = useState(0);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // User quota tracking
  const [userQuota, setUserQuota] = useState({ current: 0, max: 2 });
  const [quotaLoading, setQuotaLoading] = useState(false);

  // Sample Finder state (MusicBrainz)
  const [sampleQuery, setSampleQuery] = useState('');
  const [sampleArtist, setSampleArtist] = useState('');
  const [sampleResult, setSampleResult] = useState(null);
  const [sampleLoading, setSampleLoading] = useState(false);
  const [artistRecordings, setArtistRecordings] = useState(null); // { artist, recordings[] }
  const [selectedRecordingMbid, setSelectedRecordingMbid] = useState(null); // loading detail for this

  // User tracks management - Phase 4
  const [showMyTracks, setShowMyTracks] = useState(false);
  const [userTracks, setUserTracks] = useState([]);
  const [tracksLoading, setTracksLoading] = useState(false);

  // Phase 5: Enhanced notifications and user experience
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  
  // Emote picker state
  const [showEmotePicker, setShowEmotePicker] = useState(false);
  const [emoteTargetField, setEmoteTargetField] = useState(null); // 'newPost' or 'reply'
  const [globalEmotes, setGlobalEmotes] = useState([]);
  const [customEmotes, setCustomEmotes] = useState([]);

  // Phase 4: Reaction and Presence state
  const [showReactionPicker, setShowReactionPicker] = useState(null); // post/reply ID
  const [userPresence, setUserPresence] = useState({}); // {user_id: {online: bool}}

  // Reply modal state
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replyPostId, setReplyPostId] = useState(null);
  const [replyPostTitle, setReplyPostTitle] = useState('');
  const [replyContent, setReplyContent] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  
  // Edit state
  const [editingPostId, setEditingPostId] = useState(null);
  const [editingPostTitle, setEditingPostTitle] = useState('');
  const [editingPostContent, setEditingPostContent] = useState('');
  const [editingReplyId, setEditingReplyId] = useState(null);
  const [editingReplyContent, setEditingReplyContent] = useState('');
  
  // Public/Private reply state
  const [replyType, setReplyType] = useState('public'); // 'public' or 'private'
  const [replyPassword, setReplyPassword] = useState('');

  // Reply viewing state
  const [showRepliesModal, setShowRepliesModal] = useState(false);
  const [currentPostReplies, setCurrentPostReplies] = useState([]);
  const [currentPostId, setCurrentPostId] = useState(null);
  const [currentPostTitle, setCurrentPostTitle] = useState('');
  const [repliesType, setRepliesType] = useState('public'); // 'public' or 'private'
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [replyCounts, setReplyCounts] = useState({ public: 0, private: 0, is_post_author: false });
  
  // Password verification state for private replies
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [verificationPassword, setVerificationPassword] = useState('');
  const [verifyingPassword, setVerifyingPassword] = useState(false);

  // Video categories state (will be loaded from API)
  const [videoCategories, setVideoCategories] = useState([
    { id: 'all', name: 'All Videos', count: 0 },
    { id: 'tutorials', name: 'Tutorials', count: 0 },
    { id: 'studio-tours', name: 'Studio Tours', count: 0 },
    { id: 'gear-reviews', name: 'Gear Reviews', count: 0 },
    { id: 'track-breakdowns', name: 'Track Breakdowns', count: 0 }
  ]);

  // Mock data for initial implementation
  const mockContent = {
    tips: [
      {
        id: 1,
        title: "Mastering Electronic Music: EQ Techniques",
        author: "DJ Producer",
        date: "2025-10-04",
        description: "Essential EQ tips for getting your electronic tracks radio-ready",
        type: "tutorial",
        tags: ["Mixing", "Mastering", "Electronic"]
      },
      {
        id: 2,
        title: "Creating Powerful Drop Sections",
        author: "BassMaster",
        date: "2025-10-03",
        description: "How to build energy and create impactful drops that move the dancefloor",
        type: "technique",
        tags: ["Production", "Arrangement", "Bass"]
      }
    ],
    demos: [
      {
        id: 1,
        title: "Spinnin' Records - Demo Submissions",
        url: "https://spinninrecords.com/demos",
        description: "Submit your tracks to one of the world's leading electronic music labels",
        deadline: "Rolling submissions",
        genres: ["Progressive House", "Future House", "Tech House"]
      },
      {
        id: 2,
        title: "Anjunabeats - Demo Portal",
        url: "https://anjunabeats.com/demos",
        description: "Submit progressive and trance tracks to Above & Beyond's label",
        deadline: "Open submissions",
        genres: ["Trance", "Progressive", "Melodic Techno"]
      }
    ],
    labels: [
      {
        id: 1,
        name: "Toolroom Records",
        genre: "Tech House",
        contact: "demos@toolroomrecords.com",
        description: "Mark Knight's label focusing on underground tech house",
        website: "https://toolroomrecords.com"
      },
      {
        id: 2,
        name: "Defected Records",
        genre: "House",
        contact: "demos@defected.com",  
        description: "Legendary house music label with global reach",
        website: "https://defected.com"
      }
    ],
    news: [
      {
        id: 1,
        title: "New Ableton Live 12 Features for Electronic Producers",
        date: "2025-10-05",
        summary: "Latest updates include enhanced MIDI effects and new synthesizers",
        source: "Electronic Musician Magazine"
      },
      {
        id: 2,
        title: "ADE 2025 Conference Dates Announced",
        date: "2025-10-04",
        summary: "Amsterdam Dance Event returns with expanded producer workshops",
        source: "DJ Mag"
      }
    ]
  };

  const categories = [
    { id: 'videos', name: 'Video Board', icon: <Youtube size={16} />, count: videos.length },
    { id: 'forum', name: 'Forum', icon: <Users size={16} />, count: 3, description: 'Q&A, Stories & Advice' },
    { id: 'samples', name: 'Track DNA', icon: <Music size={16} />, count: '', description: 'MusicBrainz credits, remixes & sample lookup' },
    { id: 'tips', name: 'Tips & Tricks', icon: <Zap size={16} />, count: mockContent.tips.length },
    { id: 'demos', name: 'Demo Drops & Contests', icon: <Music size={16} />, count: mockContent.demos.length },
    { id: 'labels', name: 'Connect', icon: <Star size={16} />, count: '', description: 'Labels & Collaborators' },
    { id: 'news', name: 'Industry News', icon: <Calendar size={16} />, count: mockContent.news.length }
  ];

  // Load videos and categories on component mount - optimized to prevent excessive API calls
  useEffect(() => {
    if (selectedCategory === 'videos') {
      loadVideos();
    }
    // Load emotes when component opens
    if (isOpen) {
      loadEmotes();
    }
  }, [selectedCategory, isOpen]);
  
  // Load emotes
  const loadEmotes = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Fetch global emotes
      const globalResponse = await fetch(`${BACKEND_URL}/api/emotes/global`);
      if (globalResponse.ok) {
        const global = await globalResponse.json();
        setGlobalEmotes(global);
      }
      
      // Fetch user's custom emotes (if logged in)
      const token = localStorage.getItem('beatseek_token');
      if (token) {
        const customResponse = await fetch(`${BACKEND_URL}/api/emotes/my-custom`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (customResponse.ok) {
          const custom = await customResponse.json();
          setCustomEmotes(custom);
        }
      }
    } catch (error) {
      console.error('Failed to load emotes:', error);
    }
  };

  // Separate effect for category/search changes to prevent excessive calls
  useEffect(() => {
    if (selectedCategory === 'videos') {
      // Debounce search to prevent API call on every keystroke
      const timeoutId = setTimeout(() => {
        loadVideos();
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [selectedVideoCategory, searchTerm]);

  // Load video categories from API
  const loadVideoCategories = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/videos/categories`);
      
      if (response.ok) {
        const apiCategories = await response.json();
        console.log('📂 Received categories from API:', apiCategories);
        setVideoCategories(apiCategories);
      } else {
        console.warn('Failed to load video categories from API');
      }
    } catch (error) {
      console.error('Error loading video categories:', error);
    }
  };

  // Load videos from API (real YouTube data)
  const loadVideos = async (forceRefresh = false) => {
    if (forceRefresh) {
      setVideos([]); // Clear current videos first
    }
    
    setLoading(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : '';
      const categoryParam = selectedVideoCategory !== 'all' ? `&category=${selectedVideoCategory}` : '';
      
      // Add cache-busting timestamp for force refresh
      const cacheBuster = forceRefresh ? `&_t=${Date.now()}` : '';
      const apiUrl = `${BACKEND_URL}/api/videos?${categoryParam}${searchParam}${cacheBuster}`;
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Cache-Control': forceRefresh ? 'no-cache, no-store, must-revalidate' : 'default',
          'Pragma': forceRefresh ? 'no-cache' : 'default'
        }
      });
      
      if (response.ok) {
        const apiVideos = await response.json();
        
        // Force update by clearing first then setting
        if (forceRefresh) {
          setTimeout(() => {
            setVideos(apiVideos || []);
          }, 100);
        } else {
          setVideos(apiVideos || []);
        }
      } else {
        // API request failed - handle silently to prevent console spam
        setVideos([]); // Show empty state instead of mock data
      }
    } catch (error) {
      // Handle error silently to prevent console spam
      setVideos([]); // Show empty state instead of mock data
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort videos
  const getFilteredAndSortedVideos = () => {
    let filtered = videos.filter(video => {
      // Category filter
      if (selectedVideoCategory !== 'all' && video.category !== selectedVideoCategory) {
        return false;
      }
      
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return video.title.toLowerCase().includes(searchLower) ||
               video.description.toLowerCase().includes(searchLower) ||
               video.tags.some(tag => tag.toLowerCase().includes(searchLower));
      }
      
      return true;
    });

    // Sort videos
    switch (sortBy) {
      case 'trending':
        return filtered.sort((a, b) => (b.trending || 0) - (a.trending || 0));
      case 'newest':
        return filtered.sort((a, b) => new Date(b.uploaded_at || b.uploadedAt) - new Date(a.uploaded_at || a.uploadedAt));
      case 'most-viewed':
        return filtered.sort((a, b) => (b.views || 0) - (a.views || 0));
      case 'most-liked':
        return filtered.sort((a, b) => (b.likes || 0) - (a.likes || 0));
      default:
        return filtered;
    }
  };

  const filteredVideos = getFilteredAndSortedVideos();

  // Forum functions
  const handleForumCategoryClick = (category) => {
    setSelectedForum(category);
    loadForumPosts(category);
  };

  const loadForumPosts = async (category) => {
    setForumLoading(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/forum/posts?category=${category}`);
      
      if (response.ok) {
        const posts = await response.json();
        
        // Enhance posts with detailed reply counts
        const token = localStorage.getItem('beatseek_token');
        if (token) {
          const enhancedPosts = await Promise.all(
            posts.map(async (post) => {
              try {
                const countsResponse = await fetch(`${BACKEND_URL}/api/forum/posts/${post.id}/replies/counts`, {
                  method: 'GET',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  }
                });
                
                if (countsResponse.ok) {
                  const counts = await countsResponse.json();
                  return {
                    ...post,
                    public_replies_count: counts.public,
                    private_replies_count: counts.private,
                    is_post_author: counts.is_post_author
                  };
                }
              } catch (error) {
                console.error(`Failed to fetch counts for post ${post.id}:`, error);
              }
              return post;
            })
          );
          setForumPosts(enhancedPosts);
        } else {
          setForumPosts(posts);
        }
      } else {
        console.warn('Failed to load forum posts, using mock data');
        // Mock data for now
        setForumPosts([
          {
            id: 1,
            title: category === 'qa' ? "How do I get that punchy kick drum sound?" : 
                   category === 'stories' ? "My journey from bedroom producer to getting signed" :
                   "Feedback on my new Progressive House track",
            content: category === 'qa' ? "I've been struggling to get that tight, punchy kick that cuts through the mix..." :
                     category === 'stories' ? "Started 3 years ago with just my laptop and a dream..." :
                     "Here's my latest track I've been working on. Would love some honest feedback!",
            author: category === 'qa' ? "BeatMaker101" : 
                    category === 'stories' ? "ProducerLife" : "HouseHead23",
            replies: category === 'qa' ? 12 : category === 'stories' ? 8 : 15,
            likes: category === 'qa' ? 24 : category === 'stories' ? 31 : 18,
            created_at: "2 hours ago"
          }
        ]);
      }
    } catch (error) {
      console.error('Error loading forum posts:', error);
      setForumPosts([]);
    } finally {
      setForumLoading(false);
    }
  };

  const openNewPostModal = () => {
    setNewPostTitle('');
    setNewPostContent('');
    setShowNewPostModal(true);
  };

  const closeNewPostModal = () => {
    setShowNewPostModal(false);
    setNewPostTitle('');
    setNewPostContent('');
    // Reset audio state
    setAudioUploadId(null);
    setAudioFile(null);
    setAudioPreviewUrl(null);
    setTermsAccepted(false);
  };

  const submitNewPost = async () => {
    if (!newPostTitle.trim() || !newPostContent.trim()) {
      alert('Please fill in both title and content');
      return;
    }

    setSubmittingPost(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('beatseek_token');
      
      const response = await fetch(`${BACKEND_URL}/api/forum/posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: newPostTitle.trim(),
          content: newPostContent.trim(),
          category: selectedForum,
          audio_preview_id: audioUploadId // Include audio preview ID if available
        })
      });

      if (response.ok) {
        const newPost = await response.json();
        console.log('✅ Post created successfully:', newPost);
        
        // Check if post was flagged by AI moderation
        if (newPost.moderation_status === 'pending_moderation') {
          alert('⏳ Your post has been sent to admin for approval\n\nOur AI detected potentially inappropriate content. An admin will review your post shortly.');
          console.log('🚨 Post flagged for moderation:', newPost.moderation_reason);
        } else {
          // Add the new post to the current posts list only if approved
          setForumPosts(prevPosts => [newPost, ...prevPosts]);
          alert('Post created successfully!');
        }
        
        // Close the modal
        closeNewPostModal();
      } else {
        console.error('Failed to create post:', response.status);
        alert('Failed to create post. Please try again.');
      }
    } catch (error) {
      console.error('Error creating post:', error);
      alert('Error creating post. Please try again.');
    } finally {
      setSubmittingPost(false);
    }
  };

  const renderNewPostModal = () => {
    if (!showNewPostModal) return null;

    const categoryInfo = {
      qa: { name: 'Q&A', placeholder: 'Ask your production question...', titlePlaceholder: 'What\'s your question?' },
      stories: { name: 'Our Journey', placeholder: 'Share your producer journey...', titlePlaceholder: 'Your journey title...' },
      feedback: { name: 'Track Feedback', placeholder: 'Describe your track and what kind of feedback you\'re looking for...', titlePlaceholder: 'Track name or feedback request...' }
    };

    const info = categoryInfo[selectedForum] || { name: 'Forum', placeholder: 'Write your post...', titlePlaceholder: 'Post title...' };

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900 rounded-2xl border border-purple-500/20 w-full max-w-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-white">New Post - {info.name}</h3>
            <button
              onClick={closeNewPostModal}
              className="text-gray-400 hover:text-white transition-colors duration-200"
            >
              <X size={24} />
            </button>
          </div>

          <div className="space-y-4">
            {/* Title Input */}
            <div>
              <input
                type="text"
                placeholder={info.titlePlaceholder}
                value={newPostTitle}
                onChange={(e) => setNewPostTitle(e.target.value)}
                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500/50 focus:bg-gray-800/70 transition-all duration-300"
                maxLength={200}
              />
              <div className="text-xs text-gray-400 mt-1">
                {newPostTitle.length}/200 characters
              </div>
            </div>

            {/* Content Textarea */}
            <div>
              <div className="relative">
                <EmoteInput
                  placeholder={info.placeholder}
                  value={newPostContent}
                  onChange={setNewPostContent}
                  globalEmotes={globalEmotes}
                  customEmotes={customEmotes}
                  className="w-full px-4 py-3 pr-12 bg-gray-800/50 border border-gray-700/50 rounded-lg text-white placeholder-gray-400 focus:border-purple-500/50 focus:bg-gray-800/70 transition-all duration-300 resize-none min-h-[200px]"
                />
                {/* Emote Picker Button */}
                <button
                  type="button"
                  onClick={() => {
                    setEmoteTargetField('newPost');
                    setShowEmotePicker(true);
                  }}
                  className="absolute bottom-3 right-3 p-2 text-gray-400 hover:text-purple-400 hover:bg-purple-500/20 rounded-lg transition-all duration-200"
                  title="Add Emote"
                >
                  <span className="text-xl">😊</span>
                </button>
              </div>
              <div className="text-xs text-gray-400 mt-1">
                {newPostContent.length}/2000 characters
              </div>
            </div>

            {/* Track Preview Section - Only for feedback category */}
            {selectedForum === 'feedback' && (
              <div className="border border-gray-700/50 rounded-lg p-4 bg-gray-800/30">
                {/* Header Row */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                    </svg>
                    <h4 className="text-white font-semibold">Track Preview</h4>
                  </div>
                  
                  {/* Right side controls */}
                  <div className="flex items-center gap-2">
                    {/* Quota indicator */}
                    {quotaLoading ? (
                      <div className="animate-pulse bg-gray-600 h-6 w-20 rounded"></div>
                    ) : (
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        userQuota.current >= userQuota.max 
                          ? 'bg-red-500/20 text-red-300 border border-red-500/50' 
                          : 'bg-blue-500/20 text-blue-300 border border-blue-500/50'
                      }`}>
                        {userQuota.current}/{userQuota.max} used
                      </span>
                    )}
                    
                    {/* Notification Bell */}
                    <button
                      onClick={() => setShowNotifications(!showNotifications)}
                      type="button"
                      className={`relative p-1.5 rounded-md transition-all duration-300 ${
                        notifications.some(n => n.urgency === 'urgent') 
                          ? 'bg-red-500/20 border border-red-500/50 text-red-300 animate-pulse' 
                          : notifications.length > 0
                            ? 'bg-yellow-500/20 border border-yellow-500/50 text-yellow-300'
                            : 'bg-gray-600/20 border border-gray-500/50 text-gray-400 hover:text-white'
                      }`}
                      title={`${notifications.length} notifications`}
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM11 3a7 7 0 100 14h4" />
                      </svg>
                      {notifications.length > 0 && (
                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center text-[10px]">
                          {notifications.length > 9 ? '9+' : notifications.length}
                        </span>
                      )}
                    </button>

                    {/* My Tracks Button */}
                    <button
                      onClick={() => {
                        setShowMyTracks(true);
                        fetchUserTracks();
                      }}
                      type="button"
                      className="bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500/50 text-purple-300 hover:text-white px-2 py-1 rounded-md text-xs transition-all duration-300 flex items-center gap-1"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                      My Tracks
                    </button>
                  </div>
                </div>

                {/* Informational Text */}
                <div className="mb-4">
                  <p className="text-gray-400 text-sm leading-relaxed">
                    Optional: Upload a 45-second preview of your track to get better feedback from the community.
                    <span className="text-xs text-gray-500 ml-2">
                      ({userQuota.max - userQuota.current} slots remaining) • 7-day expiry
                    </span>
                  </p>
                </div>

                {/* Action Row */}
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    {!audioUploadId ? (
                      <button
                        onClick={() => {
                          if (userQuota.current >= userQuota.max) {
                            alert(`You have reached the maximum of ${userQuota.max} active track previews. Please wait for existing tracks to expire before uploading new ones.`);
                            return;
                          }
                          setShowAudioWarning(true);
                        }}
                        type="button"
                        disabled={userQuota.current >= userQuota.max}
                        className={`px-3 py-2 rounded-md text-sm transition-all duration-300 flex items-center gap-2 ${
                          userQuota.current >= userQuota.max
                            ? 'bg-gray-600/20 border border-gray-500/50 text-gray-500 cursor-not-allowed'
                            : 'bg-green-600/20 hover:bg-green-600/30 border border-green-500/50 text-green-300 hover:text-white'
                        }`}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Track Preview
                      </button>
                    ) : (
                      <div className="flex items-center gap-3">
                        <span className="text-green-400 text-sm flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Track preview added
                        </span>
                        <button
                          onClick={() => {
                            setAudioUploadId(null);
                            setAudioFile(null);
                            setAudioPreviewUrl(null);
                          }}
                          type="button"
                          className="text-red-400 hover:text-red-300 text-sm px-2 py-1 rounded hover:bg-red-500/10 transition-all duration-200"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-4">
              <button
                onClick={closeNewPostModal}
                disabled={submittingPost}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors duration-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={submitNewPost}
                disabled={submittingPost || !newPostTitle.trim() || !newPostContent.trim()}
                className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {submittingPost ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Publishing...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                    Publish Post
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // User quota functions
  const fetchUserQuota = async () => {
    try {
      setQuotaLoading(true);
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('beatseek_token');
      
      if (!token) {
        setUserQuota({ current: 0, max: 2 });
        return;
      }
      
      // Count user's active audio tracks
      const response = await fetch(`${BACKEND_URL}/api/forum/audio/user-quota`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUserQuota({
          current: data.current_count || 0,
          max: data.max_allowed || 2,
          remaining: data.remaining || 0,
          quotaFull: data.quota_full || false
        });
      } else {
        // Fallback to default quota
        setUserQuota({ current: 0, max: 2, remaining: 2, quotaFull: false });
      }
    } catch (error) {
      console.error('Error fetching user quota:', error);
      setUserQuota({ current: 0, max: 2, remaining: 2, quotaFull: false });
    } finally {
      setQuotaLoading(false);
    }
  };

  // Load quota when opening Track Feedback
  useEffect(() => {
    if (showNewPostModal && selectedForum === 'feedback') {
      fetchUserQuota();
    }
  }, [showNewPostModal, selectedForum]);

  // Forum interaction handlers
  const handleLikeClick = async (postId) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('beatseek_token');
      
      if (!token) {
        alert('Please login to like posts');
        return;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/forum/posts/${postId}/like`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Post liked successfully:', result.message);
        // Refresh forum posts to show updated like count
        if (selectedForum) {
          loadForumPosts(selectedForum);
        }
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('❌ Failed to like post:', errorData);
        
        if (response.status === 401) {
          alert('Authentication failed. Please try logging out and back in.');
        } else if (response.status === 403) {
          alert('Permission denied. Please check your account status.');
        } else {
          alert(`Error liking post: ${errorData.detail || 'Server error'}`);
        }
      }
    } catch (error) {
      console.error('💥 Network error liking post:', error);
      alert('Network error. Please check your connection and try again.');
    }
  };

  const handleReplyClick = (postId, postTitle) => {
    setReplyPostId(postId);
    setReplyPostTitle(postTitle);
    setReplyContent('');
    setReplyType('public'); // Reset to public
    setShowReplyModal(true);
  };

  const submitReply = async (postId, content) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('beatseek_token');
      
      if (!token) {
        alert('Please login to reply to posts');
        return;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/forum/posts/${postId}/replies`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          content: content,
          reply_type: replyType
        })
      });

      if (response.ok) {
        const replyData = await response.json();
        
        // Check if reply was flagged by AI moderation
        if (replyData.moderation_status === 'pending_moderation') {
          alert('⏳ Your reply has been sent to admin for approval\n\nOur AI detected potentially inappropriate content. An admin will review your reply shortly.');
          console.log('🚨 Reply flagged for moderation:', replyData.moderation_reason);
        }
        
        // Refresh forum posts to show updated reply count (if approved)
        if (selectedForum) {
          loadForumPosts(selectedForum);
        }
        console.log('✅ Reply posted successfully');
        return true; // Success indicator
      } else {
        const errorData = await response.json();
        console.error('Failed to post reply:', errorData);
        
        if (response.status === 401) {
          alert('Please login to reply to posts');
        } else {
          alert(`Failed to post reply: ${errorData.detail || 'Please try again'}`);
        }
        return false;
      }
    } catch (error) {
      console.error('Error posting reply:', error);
      alert('Network error posting reply. Please check your connection.');
      return false;
    }
  };

  // Delete forum post
  const deleteForumPost = async (postId) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('beatseek_token');
      
      if (!token) {
        alert('Please login to delete posts');
        return false;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/forum/posts/${postId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Refresh forum posts to remove deleted post
        if (selectedForum) {
          loadForumPosts(selectedForum);
        }
        console.log('✅ Post deleted successfully');
        return true;
      } else {
        const errorData = await response.json();
        console.error('Failed to delete post:', errorData);
        alert(`Failed to delete post: ${errorData.detail || 'Please try again'}`);
        return false;
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      alert('Network error deleting post. Please check your connection.');
      return false;
    }
  };

  // Delete forum reply
  const deleteForumReply = async (postId, replyId) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('beatseek_token');
      
      if (!token) {
        alert('Please login to delete replies');
        return false;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/forum/posts/${postId}/replies/${replyId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Refresh replies to remove deleted reply
        if (currentPostReplies.length > 0) {
          loadReplies(postId);
        }
        console.log('✅ Reply deleted successfully');
        return true;
      } else {
        const errorData = await response.json();
        console.error('Failed to delete reply:', errorData);
        alert(`Failed to delete reply: ${errorData.detail || 'Please try again'}`);
        return false;
      }
    } catch (error) {
      console.error('Error deleting reply:', error);
      alert('Network error deleting reply. Please check your connection.');
      return false;
    }
  };

  // Edit forum post
  const editForumPost = async (postId, title, content) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('beatseek_token');
      
      if (!token) {
        alert('Please login to edit posts');
        return false;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/forum/posts/${postId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: title,
          content: content,
          category: selectedForum // Keep the same category
        })
      });

      if (response.ok) {
        // Refresh forum posts to show updated post
        if (selectedForum) {
          loadForumPosts(selectedForum);
        }
        setEditingPostId(null); // Exit edit mode
        console.log('✅ Post edited successfully');
        return true;
      } else {
        const errorData = await response.json();
        console.error('Failed to edit post:', errorData);
        alert(`Failed to edit post: ${errorData.detail || 'Please try again'}`);
        return false;
      }
    } catch (error) {
      console.error('Error editing post:', error);
      alert('Network error editing post. Please check your connection.');
      return false;
    }
  };

  // Edit forum reply
  const editForumReply = async (postId, replyId, content) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('beatseek_token');
      
      if (!token) {
        alert('Please login to edit replies');
        return false;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/forum/posts/${postId}/replies/${replyId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          content: content,
          reply_type: 'public' // Keep the same type
        })
      });

      if (response.ok) {
        // Refresh replies to show updated reply
        if (currentPostReplies.length > 0) {
          loadReplies(postId);
        }
        setEditingReplyId(null); // Exit edit mode
        console.log('✅ Reply edited successfully');
        return true;
      } else {
        const errorData = await response.json();
        console.error('Failed to edit reply:', errorData);
        alert(`Failed to edit reply: ${errorData.detail || 'Please try again'}`);
        return false;
      }
    } catch (error) {
      console.error('Error editing reply:', error);
      alert('Network error editing reply. Please check your connection.');
      return false;
    }
  };

  // Reply viewing functions
  const fetchReplyCounts = async (postId) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('beatseek_token');
      
      if (!token) {
        setReplyCounts({ public: 0, private: 0, is_post_author: false });
        return;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/forum/posts/${postId}/replies/counts`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setReplyCounts(data);
      } else {
        setReplyCounts({ public: 0, private: 0, is_post_author: false });
      }
    } catch (error) {
      console.error('Error fetching reply counts:', error);
      setReplyCounts({ public: 0, private: 0, is_post_author: false });
    }
  };

  const loadReplies = async (postId, type = 'public') => {
    if (type === 'private') {
      // For private replies, show password modal first
      setShowPasswordModal(true);
      return;
    }
    
    setLoadingReplies(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('beatseek_token');
      
      if (!token) {
        alert('Please login to view replies');
        return;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/forum/posts/${postId}/replies/public`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const replies = await response.json();
        setCurrentPostReplies(replies);
        setRepliesType(type);
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        alert(`Error loading replies: ${errorData.detail || 'Server error'}`);
        setCurrentPostReplies([]);
      }
    } catch (error) {
      console.error('Error loading replies:', error);
      alert('Network error loading replies. Please try again.');
      setCurrentPostReplies([]);
    } finally {
      setLoadingReplies(false);
    }
  };

  const verifyPasswordAndLoadPrivateReplies = async () => {
    if (!verificationPassword.trim()) {
      alert('Please enter your password');
      return;
    }
    
    setVerifyingPassword(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('beatseek_token');
      
      if (!token) {
        alert('Please login to view private replies');
        return;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/forum/posts/${currentPostId}/replies/private/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          password: verificationPassword
        })
      });

      if (response.ok) {
        const replies = await response.json();
        setCurrentPostReplies(replies);
        setRepliesType('private');
        setShowPasswordModal(false);
        setVerificationPassword('');
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Invalid password' }));
        if (response.status === 401) {
          alert('Invalid password. Please try again.');
        } else if (response.status === 403) {
          alert('You can only view private replies for your own posts');
        } else {
          alert(`Error: ${errorData.detail || 'Failed to verify password'}`);
        }
      }
    } catch (error) {
      console.error('Error verifying password:', error);
      alert('Network error. Please try again.');
    } finally {
      setVerifyingPassword(false);
    }
  };

  const openRepliesModal = async (postId, postTitle, type = 'public') => {
    setCurrentPostId(postId);
    setCurrentPostTitle(postTitle);
    setShowRepliesModal(true);
    
    // Fetch reply counts first
    await fetchReplyCounts(postId);
    
    // Then load the specific type of replies
    await loadReplies(postId, type);
  };

  // Fetch user's active tracks
  const fetchUserTracks = async () => {
    try {
      setTracksLoading(true);
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('beatseek_token');
      
      if (!token) {
        setUserTracks([]);
        return;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/forum/audio/user-tracks`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUserTracks(data.tracks || []);
      } else {
        console.error('Failed to fetch user tracks');
        setUserTracks([]);
      }
    } catch (error) {
      console.error('Error fetching user tracks:', error);
      setUserTracks([]);
    } finally {
      setTracksLoading(false);
    }
  };

  const deleteUserTrack = async (trackId) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('beatseek_token');
      
      const response = await fetch(`${BACKEND_URL}/api/forum/audio/${trackId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Refresh tracks list and quota
        fetchUserTracks();
        fetchUserQuota();
        alert('Track deleted successfully!');
      } else {
        alert('Failed to delete track. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting track:', error);
      alert('Error deleting track. Please try again.');
    }
  };

  const formatTimeRemaining = (track) => {
    if (track.expired) return 'Expired';
    if (track.days_remaining > 0) return `${track.days_remaining}d remaining`;
    if (track.hours_remaining > 0) return `${track.hours_remaining}h remaining`;
    return 'Expires soon';
  };

  // Phase 5: Enhanced notification system
  const checkTrackNotifications = (tracks) => {
    const newNotifications = [];
    
    tracks.forEach(track => {
      if (track.days_remaining <= 1 && !track.expired) {
        const urgency = track.days_remaining === 0 ? 'urgent' : 'warning';
        newNotifications.push({
          id: track.id,
          type: 'expiration',
          urgency: urgency,
          title: `Track expiring ${track.days_remaining === 0 ? 'today' : 'tomorrow'}`,
          message: `"${track.original_filename}" ${track.days_remaining === 0 ? 'expires today' : 'expires in 1 day'}`,
          trackId: track.id,
          timestamp: new Date().toISOString()
        });
      }
    });

    setNotifications(prev => {
      // Remove old notifications for the same tracks
      const filtered = prev.filter(notif => !newNotifications.some(newNotif => newNotif.trackId === notif.trackId));
      return [...filtered, ...newNotifications];
    });

    // Show notification badge if there are urgent notifications
    if (newNotifications.some(n => n.urgency === 'urgent')) {
      setShowNotifications(true);
    }
  };

  // Phase 4: Reaction functions
  const addPostReaction = async (postId, emoji) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('beatseek_token');
      
      const response = await fetch(`${BACKEND_URL}/api/forum/posts/${postId}/reactions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ emoji })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Post reaction added:', data);
        // Update post reactions in local state
        setForumPosts(prev => prev.map(post => 
          post.id === postId ? { ...post, reactions: data.reactions } : post
        ));
      }
    } catch (error) {
      console.error('Error adding post reaction:', error);
    }
  };

  const addReplyReaction = async (replyId, emoji) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('beatseek_token');
      
      const response = await fetch(`${BACKEND_URL}/api/forum/replies/${replyId}/reactions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ emoji })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Reply reaction added:', data);
        // Reload posts to refresh replies with new reactions
        if (selectedForum) {
          loadForumPosts(selectedForum);
        }
      }
    } catch (error) {
      console.error('Error adding reply reaction:', error);
    }
  };

  // Phase 4: Presence functions
  const fetchPresence = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      // Get unique user IDs from forum posts
      const userIds = [...new Set(forumPosts.map(post => post.author_id))];
      if (userIds.length === 0) return;
      
      const response = await fetch(`${BACKEND_URL}/api/presence/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userIds)
      });

      if (response.ok) {
        const presenceData = await response.json();
        setUserPresence(presenceData);
      }
    } catch (error) {
      console.error('Error fetching presence:', error);
    }
  };

  // Check notifications when tracks are loaded
  useEffect(() => {
    if (userTracks.length > 0) {
      checkTrackNotifications(userTracks);
    }
  }, [userTracks]);

  // Fetch presence when forum posts change
  useEffect(() => {
    if (forumPosts.length > 0 && selectedForum) {
      fetchPresence();
      // Update presence every 60 seconds
      const presenceInterval = setInterval(() => {
        fetchPresence();
      }, 60000);
      
      return () => clearInterval(presenceInterval);
    }
  }, [forumPosts, selectedForum]);

  // Audio handling functions
  const handleAudioFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size
      if (file.size > 10 * 1024 * 1024) {
        alert('File size too large. Maximum size is 10MB.');
        return;
      }

      setAudioFile(file);
      
      // Create preview URL
      const url = URL.createObjectURL(file);
      setAudioPreviewUrl(url);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const playClipPreview = () => {
    if (audioPlayer) {
      audioPlayer.currentTime = clipStartTime;
      audioPlayer.play();
      
      // Stop playback after 45 seconds or at clip end
      setTimeout(() => {
        if (audioPlayer && !audioPlayer.paused) {
          audioPlayer.pause();
        }
      }, (clipEndTime - clipStartTime) * 1000);
    }
  };

  const handleAudioUpload = async () => {
    if (!audioFile || !termsAccepted) return;

    setAudioUploading(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('beatseek_token');
      
      const formData = new FormData();
      formData.append('file', audioFile);

      const response = await fetch(`${BACKEND_URL}/api/forum/audio/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Audio uploaded successfully:', result);
        
        setAudioUploadId(result.id);
        
        // Create clip with start time
        const clipResponse = await fetch(`${BACKEND_URL}/api/forum/audio/${result.id}/clip`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            clip_start_time: clipStartTime
          })
        });

        if (clipResponse.ok) {
          console.log('✅ Audio clip created successfully');
          setShowAudioWarning(false);
          // Refresh user quota
          fetchUserQuota();
          alert('Track preview added successfully! You can now create your post.');
        } else {
          console.error('Failed to create audio clip');
          alert('Failed to process audio clip. Please try again.');
        }
      } else {
        const errorData = await response.json();
        console.error('Failed to upload audio:', errorData);
        alert(`Failed to upload audio: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error uploading audio:', error);
      alert('Error uploading audio. Please try again.');
    } finally {
      setAudioUploading(false);
    }
  };

  const renderPasswordVerificationModal = () => {
    if (!showPasswordModal) return null;

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" style={{zIndex: 9700}}>
        <div className="bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900 rounded-2xl border border-purple-500/20 w-full max-w-md p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Verify Your Identity</h3>
                <p className="text-sm text-gray-400">Enter your account password to view private feedback</p>
              </div>
            </div>
            <button
              onClick={() => {
                setShowPasswordModal(false);
                setVerificationPassword('');
              }}
              className="text-gray-400 hover:text-white transition-colors duration-200"
              disabled={verifyingPassword}
            >
              <X size={24} />
            </button>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-white mb-2">
              Your Account Password
            </label>
            <input
              type="password"
              value={verificationPassword}
              onChange={(e) => setVerificationPassword(e.target.value)}
              placeholder="Enter your login password..."
              disabled={verifyingPassword}
              onKeyPress={(e) => e.key === 'Enter' && verifyPasswordAndLoadPrivateReplies()}
              className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all duration-300"
              autoFocus
            />
            <p className="text-xs text-gray-400 mt-2">
              This verifies you are the post author and authorized to view private feedback.
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => {
                setShowPasswordModal(false);
                setVerificationPassword('');
              }}
              disabled={verifyingPassword}
              className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors duration-300 text-sm"
            >
              Cancel
            </button>
            <button
              onClick={verifyPasswordAndLoadPrivateReplies}
              disabled={verifyingPassword || !verificationPassword.trim()}
              className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg transition-colors duration-300 text-sm flex items-center justify-center gap-2"
            >
              {verifyingPassword ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Verifying...
                </>
              ) : (
                'Verify & View'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderRepliesViewModal = () => {
    if (!showRepliesModal) return null;

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" style={{zIndex: 9600}}>
        <div className="bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900 rounded-2xl border border-purple-500/20 w-full max-w-4xl max-h-[90vh] overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b border-gray-700/50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">
                  {repliesType === 'public' ? '🌐 Public' : '🔒 Private'} Feedback
                </h3>
                <p className="text-sm text-gray-400">{currentPostTitle}</p>
              </div>
            </div>
            <button
              onClick={() => {
                setShowRepliesModal(false);
                setCurrentPostReplies([]);
                setCurrentPostId(null);
              }}
              className="text-gray-400 hover:text-white transition-colors duration-200"
            >
              <X size={24} />
            </button>
          </div>

          {/* Reply Type Tabs */}
          <div className="flex border-b border-gray-700/50">
            <button
              onClick={() => loadReplies(currentPostId, 'public')}
              disabled={loadingReplies}
              className={`flex-1 px-6 py-3 text-sm font-medium transition-colors duration-200 ${
                repliesType === 'public'
                  ? 'text-green-400 border-b-2 border-green-400 bg-green-500/10'
                  : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800/50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Public Feedback ({replyCounts.public})
              </div>
            </button>
            
            {replyCounts.is_post_author && (
              <button
                onClick={() => loadReplies(currentPostId, 'private')}
                disabled={loadingReplies}
                className={`flex-1 px-6 py-3 text-sm font-medium transition-colors duration-200 ${
                  repliesType === 'private'
                    ? 'text-purple-400 border-b-2 border-purple-400 bg-purple-500/10'
                    : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800/50'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Private Feedback ({replyCounts.private})
                </div>
              </button>
            )}
          </div>

          {/* Replies Content */}
          <div className="flex-1 overflow-y-auto max-h-[60vh] p-6">
            {loadingReplies ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                <span className="ml-3 text-gray-400">Loading feedback...</span>
              </div>
            ) : currentPostReplies.length > 0 ? (
              <div className="space-y-4">
                {currentPostReplies.map((reply, index) => (
                  <div key={reply.id} className="bg-gray-800/30 border border-gray-700/50 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {/* Avatar with presence dot */}
                        <div className="relative">
                          <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center text-white text-sm font-semibold overflow-hidden">
                            {reply.author_avatar ? (
                              <img 
                                src={reply.author_avatar.startsWith('/uploads/') ? `${process.env.REACT_APP_BACKEND_URL}/api${reply.author_avatar}` : reply.author_avatar} 
                                alt={reply.author_name} 
                                className="w-full h-full rounded-full object-cover"
                              />
                            ) : (
                              reply.author_name?.charAt(0).toUpperCase() || 'U'
                            )}
                          </div>
                          {/* Presence dot */}
                          <div className="absolute -bottom-0.5 -right-0.5">
                            <PresenceDot 
                              online={(() => {
                                const currentUser = JSON.parse(localStorage.getItem('beatseek_user') || '{}');
                                // Current user is always online
                                return reply.author_id === currentUser.id ? true : (userPresence[reply.author_id]?.online || false);
                              })()}
                              size="small" 
                            />
                          </div>
                        </div>
                        <div>
                          <h4 className="font-semibold text-white text-sm">{reply.author_name || 'Anonymous'}</h4>
                          <p className="text-xs text-gray-400">
                            {new Date(reply.created_at).toLocaleDateString()} at {new Date(reply.created_at).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          reply.reply_type === 'private' 
                            ? 'bg-purple-500/20 text-purple-300 border border-purple-500/50'
                            : 'bg-green-500/20 text-green-300 border border-green-500/50'
                        }`}>
                          {reply.reply_type === 'private' ? '🔒 Private' : '🌐 Public'}
                        </span>
                        
                        {/* Whisper button - for all users except self */}
                        {(() => {
                          const currentUser = JSON.parse(localStorage.getItem('beatseek_user') || '{}');
                          const isOwnReply = reply.author_id === currentUser.id || reply.author === currentUser.email;
                          return !isOwnReply && onWhisperClick;
                        })() && (
                          <button
                            onClick={() => onWhisperClick({
                              user_id: reply.author_id,
                              user_name: reply.author_name || reply.author,
                              user_avatar: reply.author_avatar || null,
                              online: (() => {
                                const currentUser = JSON.parse(localStorage.getItem('beatseek_user') || '{}');
                                return reply.author_id === currentUser.id ? true : (userPresence[reply.author_id]?.online || false);
                              })()
                            })}
                            className="p-1.5 text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 rounded-lg transition-all duration-200"
                            title={`Whisper ${reply.author_name || reply.author}`}
                          >
                            <WhisperIcon />
                          </button>
                        )}
                        
                        {/* Reaction button */}
                        {editingReplyId !== reply.id && (
                          <div className="relative">
                            <button
                              onClick={() => setShowReactionPicker(showReactionPicker === `reply-${reply.id}` ? null : `reply-${reply.id}`)}
                              className="p-1.5 text-gray-400 hover:text-yellow-400 hover:bg-yellow-500/20 rounded-lg transition-all duration-200"
                              title="Add reaction"
                            >
                              <Smile size={16} />
                            </button>
                            {showReactionPicker === `reply-${reply.id}` && (
                              <ReactionPicker
                                onSelect={(emoji) => addReplyReaction(reply.id, emoji)}
                                onClose={() => setShowReactionPicker(null)}
                                position="bottom"
                                align="right"
                              />
                            )}
                          </div>
                        )}
                        
                        {/* Edit/Delete buttons for own replies */}
                        {(() => {
                          const currentUser = JSON.parse(localStorage.getItem('beatseek_user') || '{}');
                          return reply.author === currentUser.email;
                        })() && (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => {
                                setEditingReplyId(reply.id);
                                setEditingReplyContent(reply.content);
                              }}
                              className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-blue-500/20 rounded-lg transition-all duration-200"
                              title="Edit reply"
                            >
                              <Edit3 size={16} />
                            </button>
                            <button
                              onClick={async () => {
                                if (window.confirm('Delete this reply?')) {
                                  await deleteForumReply(reply.post_id, reply.id);
                                }
                              }}
                              className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/20 rounded-lg transition-all duration-200"
                              title="Delete reply"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    {editingReplyId === reply.id ? (
                      // Edit mode
                      <div className="space-y-3 mb-3">
                        <EmoteInput
                          value={editingReplyContent}
                          onChange={setEditingReplyContent}
                          placeholder="Reply content"
                          globalEmotes={globalEmotes}
                          customEmotes={customEmotes}
                          className="w-full bg-gray-700/50 border border-gray-600/50 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-purple-500 min-h-[80px]"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={async () => {
                              await editForumReply(reply.post_id, reply.id, editingReplyContent);
                            }}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-sm"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingReplyId(null)}
                            className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1.5 rounded-lg text-sm"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      // Normal display
                      <>
                        <div className="text-gray-300 text-sm leading-relaxed mb-2">
                          <span dangerouslySetInnerHTML={{ __html: renderEmotesInText(reply.content, globalEmotes, customEmotes) }} />
                        </div>
                        
                        {/* Reactions display */}
                        <Reactions
                          reactions={reply.reactions || []}
                          onReactionClick={(emoji) => addReplyReaction(reply.id, emoji)}
                          currentUserId={(() => {
                            const currentUser = JSON.parse(localStorage.getItem('beatseek_user') || '{}');
                            return currentUser.id;
                          })()}
                        />
                      </>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <button className="flex items-center gap-1 text-gray-400 hover:text-red-400 text-xs transition-colors duration-200">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                          </svg>
                          {reply.likes_count || 0}
                        </button>
                      </div>
                      <span className="text-xs text-gray-500">#{index + 1}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <svg className="w-16 h-16 mx-auto text-gray-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <h4 className="text-lg font-semibold text-white mb-2">
                  No {repliesType} feedback yet
                </h4>
                <p className="text-gray-400">
                  {repliesType === 'public' 
                    ? 'Be the first to share your thoughts publicly!' 
                    : 'No private feedback has been shared for this post.'}
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-gray-700/50">
            <div className="text-xs text-gray-400">
              {repliesType === 'private' ? 'Private feedback is only visible to you as the post author' : 'Public feedback is visible to the entire community'}
            </div>
            <button
              onClick={() => {
                setShowRepliesModal(false);
                setCurrentPostReplies([]);
                setCurrentPostId(null);
              }}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors duration-300 text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderReplyModal = () => {
    if (!showReplyModal) return null;

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" style={{zIndex: 9500}}>
        <div className="bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900 rounded-2xl border border-purple-500/20 w-full max-w-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Post Feedback</h3>
                <p className="text-sm text-gray-400">Share your thoughts</p>
              </div>
            </div>
            <button
              onClick={() => {
                setShowReplyModal(false);
                setReplyContent('');
                setReplyType('public');
              }}
              className="text-gray-400 hover:text-white transition-colors duration-200"
              disabled={submittingReply}
            >
              <X size={24} />
            </button>
          </div>

          {/* Original Post Info */}
          <div className="bg-gray-800/50 border border-gray-700/50 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-1.586l-4.707 4.707z" />
              </svg>
              <span className="text-sm font-medium text-green-300">Replying to:</span>
            </div>
            <h4 className="font-semibold text-white text-sm line-clamp-2">{replyPostTitle}</h4>
          </div>

          {/* Reply Type Selection */}
          <div className="bg-gray-800/30 border border-gray-700/50 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-white font-semibold flex items-center gap-2">
                <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Feedback Type
              </h4>
              <span className="text-xs text-gray-400">Choose your feedback visibility</span>
            </div>

            {/* Public/Private Toggle */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <button
                onClick={() => setReplyType('public')}
                disabled={submittingReply}
                className={`p-3 rounded-lg border transition-all duration-300 ${
                  replyType === 'public'
                    ? 'bg-green-500/20 border-green-500/50 text-green-300'
                    : 'bg-gray-700/50 border-gray-600/50 text-gray-400 hover:bg-gray-600/50 hover:text-gray-300'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="font-medium">🌐 Public</span>
                </div>
                <p className="text-xs opacity-80">Visible to everyone in the community</p>
              </button>

              <button
                onClick={() => setReplyType('private')}
                disabled={submittingReply}
                className={`p-3 rounded-lg border transition-all duration-300 ${
                  replyType === 'private'
                    ? 'bg-purple-500/20 border-purple-500/50 text-purple-300'
                    : 'bg-gray-700/50 border-gray-600/50 text-gray-400 hover:bg-gray-600/50 hover:text-gray-300'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <span className="font-medium">🔒 Private</span>
                </div>
                <p className="text-xs opacity-80">Only visible to the post author</p>
              </button>
            </div>

            {/* Private Reply Info */}
            {replyType === 'private' && (
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                <p className="text-sm text-purple-200 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Only the post author will be able to see this private feedback
                </p>
              </div>
            )}
          </div>

          {/* Reply Input */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-white mb-2">Your Reply</label>
              <div className="relative">
                <EmoteInput
                  value={replyContent}
                  onChange={setReplyContent}
                  placeholder="Share your thoughts, ask questions, or provide helpful feedback..."
                  globalEmotes={globalEmotes}
                  customEmotes={customEmotes}
                  disabled={submittingReply}
                  className="w-full h-32 px-4 py-3 pr-12 bg-gray-800/50 border border-gray-700/50 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 resize-none transition-all duration-300"
                />
                {/* Emote Picker Button */}
                <button
                  type="button"
                  onClick={() => {
                    setEmoteTargetField('reply');
                    setShowEmotePicker(true);
                  }}
                  className="absolute bottom-3 right-3 p-2 text-gray-400 hover:text-purple-400 hover:bg-purple-500/20 rounded-lg transition-all duration-200"
                  title="Add Emote"
                  disabled={submittingReply}
                >
                  <span className="text-xl">😊</span>
                </button>
              </div>
              <div className="flex justify-between items-center mt-2">
                <span className="text-xs text-gray-500">Be respectful and constructive</span>
                <span className={`text-xs ${replyContent.length > 1000 ? 'text-red-400' : 'text-gray-500'}`}>
                  {replyContent.length}/1000
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-700/50">
              <button
                onClick={() => {
                  setShowReplyModal(false);
                  setReplyContent('');
                  setReplyType('public');
                  setReplyPassword('');
                }}
                disabled={submittingReply}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors duration-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  // Validate content and private reply password
                  if (replyContent.trim() && replyContent.length <= 1000) {
                    setSubmittingReply(true);
                    await submitReply(replyPostId, replyContent.trim());
                    setSubmittingReply(false);
                    setShowReplyModal(false);
                    setReplyContent('');
                    setReplyType('public');
                  }
                }}
                disabled={submittingReply || !replyContent.trim() || replyContent.length > 1000}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-6 py-2 rounded-lg transition-all duration-300 flex items-center gap-2 min-w-[120px] justify-center"
              >
                {submittingReply ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Posting...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                    <span>Post Reply</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderNotificationsDropdown = () => {
    if (!showNotifications) return null;

    return (
      <div className="fixed top-4 right-4 w-80" style={{zIndex: 8888}}>
        <div className="bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900 rounded-xl border border-purple-500/20 shadow-2xl">
          <div className="flex items-center justify-between p-4 border-b border-gray-700/50">
            <h4 className="font-semibold text-white flex items-center gap-2">
              <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM11 3a7 7 0 100 14h4" />
              </svg>
              Track Notifications
            </h4>
            <button
              onClick={() => setShowNotifications(false)}
              className="text-gray-400 hover:text-white"
            >
              <X size={18} />
            </button>
          </div>
          
          <div className="max-h-96 overflow-y-auto">
            {notifications.length > 0 ? (
              notifications.map((notification) => (
                <div key={notification.id} className={`p-4 border-b border-gray-700/50 last:border-b-0 ${
                  notification.urgency === 'urgent' ? 'bg-red-500/10 border-l-4 border-l-red-500' : 
                  notification.urgency === 'warning' ? 'bg-yellow-500/10 border-l-4 border-l-yellow-500' : 
                  'bg-blue-500/10 border-l-4 border-l-blue-500'
                }`}>
                  <div className="flex items-start gap-3">
                    <div className={`p-1 rounded-full ${
                      notification.urgency === 'urgent' ? 'bg-red-500/20' : 
                      notification.urgency === 'warning' ? 'bg-yellow-500/20' : 
                      'bg-blue-500/20'
                    }`}>
                      <svg className={`w-4 h-4 ${
                        notification.urgency === 'urgent' ? 'text-red-400' : 
                        notification.urgency === 'warning' ? 'text-yellow-400' : 
                        'text-blue-400'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h5 className="font-semibold text-white text-sm">{notification.title}</h5>
                      <p className="text-gray-300 text-xs mt-1">{notification.message}</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-gray-500">
                          {new Date(notification.timestamp).toLocaleTimeString()}
                        </span>
                        <button
                          onClick={() => {
                            setShowMyTracks(true);
                            fetchUserTracks();
                            setShowNotifications(false);
                          }}
                          className="text-xs text-purple-400 hover:text-purple-300"
                        >
                          View Track
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-6 text-center">
                <svg className="w-12 h-12 mx-auto text-gray-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <h5 className="font-semibold text-white mb-1">All caught up!</h5>
                <p className="text-gray-400 text-sm">No track notifications at this time.</p>
              </div>
            )}
          </div>
          
          {notifications.length > 0 && (
            <div className="p-3 border-t border-gray-700/50">
              <button
                onClick={() => setNotifications([])}
                className="w-full text-xs text-gray-400 hover:text-white text-center"
              >
                Clear all notifications
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderMyTracksModal = () => {
    if (!showMyTracks) return null;

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" style={{zIndex: 7777}}>
        <div className="bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900 rounded-2xl border border-purple-500/20 w-full max-w-2xl p-6 max-h-[80vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-white">My Track Previews</h3>
            <button
              onClick={() => setShowMyTracks(false)}
              className="text-gray-400 hover:text-white transition-colors duration-200"
            >
              <X size={24} />
            </button>
          </div>

          {/* Quota Summary */}
          <div className="bg-gray-800/50 border border-gray-700/50 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-white font-semibold">Storage Usage</h4>
              <span className="text-sm text-gray-400">{userTracks.length}/2 slots used</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-3">
              <div 
                className={`h-3 rounded-full transition-all duration-500 ${
                  userTracks.length >= 2 ? 'bg-red-500' : userTracks.length >= 1 ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{width: `${(userTracks.length / 2) * 100}%`}}
              />
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Track previews automatically expire after 7 days. You can have maximum 2 active tracks.
            </p>
          </div>

          {/* Tracks List */}
          <div className="space-y-4">
            {tracksLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
              </div>
            ) : userTracks.length > 0 ? (
              userTracks.map((track) => (
                <div key={track.id} className="bg-gray-800/30 border border-gray-700/50 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                        </svg>
                        <h5 className="font-semibold text-white">{track.original_filename}</h5>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          track.expired 
                            ? 'bg-red-500/20 text-red-300 border border-red-500/50'
                            : track.days_remaining <= 1
                              ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/50'
                              : 'bg-green-500/20 text-green-300 border border-green-500/50'
                        }`}>
                          {formatTimeRemaining(track)}
                        </span>
                      </div>
                      
                      <div className="text-sm text-gray-400 mb-3">
                        <div className="flex items-center gap-4">
                          <span>Size: {track.file_size_mb}MB</span>
                          <span>Uploaded: {new Date(track.created_at).toLocaleDateString()}</span>
                        </div>
                        {track.post_info && (
                          <div className="mt-1">
                            <span className="text-purple-400">Post: "{track.post_info.title}"</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Audio Player for active tracks */}
                      {!track.expired && (
                        <div className="bg-gray-700/50 rounded-lg p-3 mb-3">
                          <audio 
                            controls 
                            className="w-full"
                            src={`${process.env.REACT_APP_BACKEND_URL}/api/forum/audio/${track.id}/play`}
                          />
                        </div>
                      )}
                    </div>
                    
                    <div className="ml-4">
                      <button
                        onClick={() => {
                          if (window.confirm(`Delete "${track.original_filename}"? This action cannot be undone.`)) {
                            deleteUserTrack(track.id);
                          }
                        }}
                        className="text-red-400 hover:text-red-300 p-2 rounded-lg hover:bg-red-500/20 transition-colors duration-300"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <svg className="w-16 h-16 mx-auto text-gray-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                </svg>
                <h4 className="text-lg font-semibold text-white mb-2">No active track previews</h4>
                <p className="text-gray-400">Upload your first track preview to get community feedback!</p>
              </div>
            )}
          </div>

          {/* Close button */}
          <div className="flex justify-end mt-6 pt-4 border-t border-gray-700/50">
            <button
              onClick={() => setShowMyTracks(false)}
              className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg transition-colors duration-300"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderAudioWarningModal = () => {
    if (!showAudioWarning) return null;

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" style={{zIndex: 9999}}>
        <div className="bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900 rounded-2xl border border-purple-500/20 w-full max-w-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-white">Add Track Preview</h3>
            <button
              onClick={() => {
                setShowAudioWarning(false);
                setAudioFile(null);
                setAudioPreviewUrl(null);
                setTermsAccepted(false);
              }}
              className="text-gray-400 hover:text-white transition-colors duration-200"
            >
              <X size={24} />
            </button>
          </div>

          <div className="space-y-4">
            {/* Warning Message */}
            <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <svg className="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <div>
                  <h4 className="font-semibold text-yellow-400">7-Day Preview Policy</h4>
                  <p className="text-yellow-200 text-sm">
                    Share your track with your fellow Beatseeker producers for up to 7 days of community feedback
                  </p>
                </div>
              </div>
            </div>

            {/* File Upload Section */}
            {!audioFile ? (
              <div className="border-2 border-dashed border-gray-600 rounded-lg p-8 text-center hover:border-purple-500/50 transition-colors duration-300">
                <div className="text-gray-400 mb-4">
                  <svg className="w-12 h-12 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <p className="text-white font-semibold mb-2">Drop your audio file here</p>
                <p className="text-gray-400 text-sm mb-4">
                  Supports MP3, WAV, M4A, AAC • Max 10MB
                </p>
                <input
                  type="file"
                  accept=".mp3,.wav,.m4a,.aac"
                  onChange={handleAudioFileSelect}
                  className="hidden"
                  id="audio-upload"
                />
                <label
                  htmlFor="audio-upload"
                  className="inline-block bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg cursor-pointer transition-colors duration-300"
                >
                  Choose File
                </label>
              </div>
            ) : (
              /* Audio Player Section */
              <div className="bg-gray-800/50 border border-gray-700/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-white font-semibold">{audioFile.name}</h4>
                  <button
                    onClick={() => {
                      setAudioFile(null);
                      setAudioPreviewUrl(null);
                      setClipStartTime(0);
                      setClipEndTime(45);
                    }}
                    className="text-red-400 hover:text-red-300"
                  >
                    <X size={16} />
                  </button>
                </div>

                {/* Audio Player */}
                {audioPreviewUrl ? (
                  <div className="space-y-3">
                    <div className="bg-gray-700/50 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-gray-400">Audio Player</span>
                        <span className="text-xs text-green-400">
                          {audioDuration > 0 ? `${formatTime(audioDuration)} loaded` : 'Loading...'}
                        </span>
                      </div>
                      <audio
                        ref={setAudioPlayer}
                        src={audioPreviewUrl}
                        onLoadedMetadata={(e) => {
                          const duration = e.target.duration;
                          console.log(`Audio loaded: ${duration} seconds`);
                          setAudioDuration(duration);
                          setClipEndTime(Math.min(45, duration));
                        }}
                        onError={(e) => {
                          console.error('Audio loading error:', e);
                        }}
                        controls
                        className="w-full"
                        preload="metadata"
                      />
                    </div>

                    {/* 45-Second Clip Selector - Only show when audio has loaded */}
                    {audioDuration > 0 && (
                      <div className="space-y-3 mt-4 p-3 bg-gray-700/30 rounded-lg border border-gray-600/50">
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-semibold text-white">Select 45-second clip:</label>
                          <span className="text-xs text-gray-400">Total: {formatTime(audioDuration)}</span>
                        </div>
                        <div className="space-y-2">
                          <input
                            type="range"
                            min="0"
                            max={Math.max(0, audioDuration - 45)}
                            value={clipStartTime}
                            onChange={(e) => {
                              const start = parseFloat(e.target.value);
                              setClipStartTime(start);
                              setClipEndTime(Math.min(start + 45, audioDuration));
                              console.log(`Clip range: ${formatTime(start)} - ${formatTime(Math.min(start + 45, audioDuration))}`);
                            }}
                            className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer slider-thumb"
                            style={{
                              background: `linear-gradient(to right, #10b981 0%, #10b981 ${(clipStartTime / Math.max(1, audioDuration - 45)) * 100}%, #4b5563 ${(clipStartTime / Math.max(1, audioDuration - 45)) * 100}%, #4b5563 100%)`
                            }}
                          />
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-4">
                              <span className="text-xs text-green-400 font-mono bg-green-500/20 px-2 py-1 rounded">
                                🎵 {formatTime(clipStartTime)} → {formatTime(clipEndTime)}
                              </span>
                              <span className="text-xs text-gray-400">
                                (45 sec preview)
                              </span>
                            </div>
                            <button
                              onClick={playClipPreview}
                              className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-md text-xs flex items-center gap-1 transition-colors duration-300"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h1m4 0h1m-6-8h1m4 0h1M7 7h10a2 2 0 012 2v6a2 2 0 01-2 2H7a2 2 0 01-2-2V9a2 2 0 012-2z" />
                              </svg>
                              Preview Clip
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Loading state for clip selector */}
                    {audioDuration === 0 && (
                      <div className="mt-4 p-3 bg-yellow-500/20 rounded-lg border border-yellow-500/50">
                        <div className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-400"></div>
                          <span className="text-yellow-300 text-sm">Loading audio metadata for clip selection...</span>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mt-4 p-3 bg-blue-500/20 rounded-lg border border-blue-500/50">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-blue-300 text-sm">Upload an audio file to select your 45-second preview clip</span>
                    </div>
                  </div>
                )}

                <div className="text-xs text-gray-500 mt-2">
                  File size: {(audioFile.size / (1024 * 1024)).toFixed(2)} MB
                </div>
              </div>
            )}

            {/* Terms Acceptance */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="terms-accept"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-1"
              />
              <label htmlFor="terms-accept" className="text-sm text-gray-300">
                I understand my track preview will expire after 7 days and will be automatically removed. 
                Maximum 2 active track previews allowed per user.
              </label>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-4">
              <button
                onClick={() => {
                  setShowAudioWarning(false);
                  setAudioFile(null);
                  setAudioPreviewUrl(null);
                  setTermsAccepted(false);
                }}
                disabled={audioUploading}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors duration-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAudioUpload}
                disabled={audioUploading || !audioFile || !termsAccepted}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {audioUploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    Add Track Preview
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderForumContent = () => {
    if (selectedForum) {
      return renderForumCategory(selectedForum);
    }

    // Main forum page with clickable categories
    return (
      <div className="forum-container">
        <div className="flex flex-col items-center justify-center py-16">
          <div className="text-purple-500 mb-6">
            <Users size={64} />
          </div>
          <h3 className="text-2xl font-semibold text-white mb-4">Producer Forum</h3>
          <p className="text-gray-400 text-center max-w-lg mb-8">
            Connect with fellow producers, share your journey, ask questions, and get advice from the community.
          </p>
          <div className="grid md:grid-cols-3 gap-6 w-full max-w-4xl">
            <div 
              onClick={() => handleForumCategoryClick('qa')}
              className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-6 text-center cursor-pointer hover:bg-gray-800/70 hover:border-purple-500/50 hover:transform hover:scale-105 transition-all duration-300 group"
            >
              <Zap className="text-yellow-400 mx-auto mb-3 group-hover:text-yellow-300" size={32} />
              <h4 className="font-semibold text-white mb-2 group-hover:text-purple-300">Q&A</h4>
              <p className="text-gray-400 text-sm group-hover:text-gray-300">Get answers to your production questions</p>
              <div className="mt-4 text-xs text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                Click to enter →
              </div>
            </div>
            <div 
              onClick={() => handleForumCategoryClick('stories')}
              className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-6 text-center cursor-pointer hover:bg-gray-800/70 hover:border-purple-500/50 hover:transform hover:scale-105 transition-all duration-300 group"
            >
              <Star className="text-purple-400 mx-auto mb-3 group-hover:text-purple-300" size={32} />
              <h4 className="font-semibold text-white mb-2 group-hover:text-purple-300">Stories</h4>
              <p className="text-gray-400 text-sm group-hover:text-gray-300">Share your producer journey and experiences</p>
              <div className="mt-4 text-xs text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                Click to enter →
              </div>
            </div>
            <div 
              onClick={() => handleForumCategoryClick('feedback')}
              className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-6 text-center cursor-pointer hover:bg-gray-800/70 hover:border-purple-500/50 hover:transform hover:scale-105 transition-all duration-300 group"
            >
              <Music className="text-green-400 mx-auto mb-3 group-hover:text-green-300" size={32} />
              <h4 className="font-semibold text-white mb-2 group-hover:text-purple-300">Track Feedback</h4>
              <p className="text-gray-400 text-sm group-hover:text-gray-300">Share track previews and get community feedback</p>
              <div className="mt-4 text-xs text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                Click to enter →
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderForumCategory = (category) => {
    const categoryInfo = {
      qa: { 
        title: 'Q&A Forum', 
        description: 'Ask questions and get answers from the producer community',
        icon: <Zap className="text-yellow-400" size={24} />,
        color: 'yellow'
      },
      stories: { 
        title: 'Our Journey', 
        description: 'Share your journey, experiences, and inspire others',
        icon: <Star className="text-purple-400" size={24} />,
        color: 'purple'
      },
      feedback: { 
        title: 'Track Feedback', 
        description: 'Get honest feedback on your tracks and help others improve',
        icon: <Music className="text-green-400" size={24} />,
        color: 'green'
      }
    };

    const info = categoryInfo[category];

    return (
      <div className="forum-category-container">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSelectedForum(null)}
              className="text-gray-400 hover:text-white transition-colors duration-200"
              title="Back to Forum Categories"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            {info.icon}
            <div>
              <h3 className="text-xl font-semibold text-white">{info.title}</h3>
              <p className="text-sm text-gray-400">{info.description}</p>
            </div>
          </div>
          
          <button 
            onClick={openNewPostModal}
            className="bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500/50 text-purple-300 hover:text-white px-4 py-2 rounded-lg text-sm transition-all duration-300 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Post
          </button>
        </div>

        {/* Posts */}
        <div className="space-y-4">
          {forumLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
            </div>
          ) : forumPosts.length > 0 ? (
            forumPosts.map((post) => (
              <div key={post.id} className="bg-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6 hover:border-purple-500/50 hover:bg-gray-800/50 transition-all duration-300">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Author info with avatar and edit/delete buttons */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {/* User Avatar with presence dot */}
                        <div className="relative">
                          <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0 overflow-hidden">
                            {post.author_avatar ? (
                              <img 
                                src={post.author_avatar.startsWith('/uploads/') ? `${process.env.REACT_APP_BACKEND_URL}/api${post.author_avatar}` : post.author_avatar} 
                                alt={post.author_name} 
                                className="w-full h-full rounded-full object-cover"
                              />
                            ) : (
                              (post.author_name || post.author)?.charAt(0)?.toUpperCase() || 'U'
                            )}
                          </div>
                          {/* Presence dot */}
                          <div className="absolute -bottom-0.5 -right-0.5">
                            <PresenceDot 
                              online={(() => {
                                const currentUser = JSON.parse(localStorage.getItem('beatseek_user') || '{}');
                                // Current user is always online
                                return post.author_id === currentUser.id ? true : (userPresence[post.author_id]?.online || false);
                              })()}
                              size="small" 
                            />
                          </div>
                        </div>
                        <span className="text-sm font-semibold text-gray-200">{post.author_name || post.author}</span>
                        <span className="text-xs text-gray-500">{new Date(post.created_at).toLocaleDateString()}</span>
                        
                        {/* Whisper button - for all users except self */}
                        {(() => {
                          const currentUser = JSON.parse(localStorage.getItem('beatseek_user') || '{}');
                          const isOwnPost = post.author_id === currentUser.id || post.author === currentUser.email;
                          return !isOwnPost && onWhisperClick;
                        })() && (
                          <button
                            onClick={() => onWhisperClick({
                              user_id: post.author_id,
                              user_name: post.author_name || post.author,
                              user_avatar: post.author_avatar || null,
                              online: (() => {
                                const currentUser = JSON.parse(localStorage.getItem('beatseek_user') || '{}');
                                return post.author_id === currentUser.id ? true : (userPresence[post.author_id]?.online || false);
                              })()
                            })}
                            className="p-1.5 text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 rounded-lg transition-all duration-200"
                            title={`Whisper ${post.author_name || post.author}`}
                          >
                            <WhisperIcon />
                          </button>
                        )}
                      </div>
                      
                      {/* Reaction and Edit/Delete buttons */}
                      <div className="flex space-x-2">
                        {/* Reaction button */}
                        {editingPostId !== post.id && (
                          <div className="relative">
                            <button
                              onClick={() => setShowReactionPicker(showReactionPicker === post.id ? null : post.id)}
                              className="p-1.5 text-gray-400 hover:text-yellow-400 hover:bg-yellow-500/20 rounded-lg transition-all duration-200"
                              title="Add reaction"
                            >
                              <Smile size={16} />
                            </button>
                            {showReactionPicker === post.id && (
                              <ReactionPicker
                                onSelect={(emoji) => addPostReaction(post.id, emoji)}
                                onClose={() => setShowReactionPicker(null)}
                                position="bottom"
                                align="right"
                              />
                            )}
                          </div>
                        )}
                        
                        {/* Edit/Delete buttons for own posts */}
                        {(() => {
                          const currentUser = JSON.parse(localStorage.getItem('beatseek_user') || '{}');
                          return post.author === currentUser.email;
                        })() && (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => {
                                setEditingPostId(post.id);
                                setEditingPostTitle(post.title);
                                setEditingPostContent(post.content);
                              }}
                              className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-blue-500/20 rounded-lg transition-all duration-200"
                              title="Edit post"
                            >
                              <Edit3 size={16} />
                            </button>
                            <button
                              onClick={async () => {
                                if (window.confirm('Delete this post? This will also delete all replies.')) {
                                  await deleteForumPost(post.id);
                                }
                              }}
                              className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/20 rounded-lg transition-all duration-200"
                              title="Delete post"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {editingPostId === post.id ? (
                      // Edit mode
                      <div className="space-y-3 mb-4">
                        <input
                          type="text"
                          value={editingPostTitle}
                          onChange={(e) => setEditingPostTitle(e.target.value)}
                          className="w-full bg-gray-700/50 border border-gray-600/50 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                          placeholder="Post title"
                        />
                        <EmoteInput
                          value={editingPostContent}
                          onChange={setEditingPostContent}
                          placeholder="Post content"
                          globalEmotes={globalEmotes}
                          customEmotes={customEmotes}
                          className="w-full bg-gray-700/50 border border-gray-600/50 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-purple-500 min-h-[100px]"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={async () => {
                              await editForumPost(post.id, editingPostTitle, editingPostContent);
                            }}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingPostId(null)}
                            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      // Normal display
                      <>
                        <h4 className="text-lg font-semibold text-white mb-2 hover:text-purple-300 cursor-pointer">
                          {post.title}
                        </h4>
                        <div className="text-gray-300 text-sm mb-4 line-clamp-2">
                          <span dangerouslySetInnerHTML={{ __html: renderEmotesInText(post.content, globalEmotes, customEmotes) }} />
                        </div>
                        
                        {/* Reactions display */}
                        <Reactions
                          reactions={post.reactions || []}
                          onReactionClick={(emoji) => addPostReaction(post.id, emoji)}
                          currentUserId={(() => {
                            const currentUser = JSON.parse(localStorage.getItem('beatseek_user') || '{}');
                            return currentUser.id;
                          })()}
                        />
                      </>
                    )}
                    
                    {/* Audio Track Preview Section */}
                    {post.audio_preview_id && (
                      <AudioPreviewPlayer audioId={post.audio_preview_id} postId={post.id} />
                    )}
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-6 text-sm text-gray-400">
                        {/* Stats or other info can go here */}
                      </div>
                      
                      {/* Interactive buttons */}
                      <div className="flex items-center gap-3">{/* Add Feedback Button */}
                        <button
                          onClick={() => handleReplyClick(post.id, post.title)}
                          className="flex items-center gap-1 text-sm text-gray-400 hover:text-green-400 transition-colors duration-200 px-2 py-1 rounded-md hover:bg-green-500/10"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Add Feedback
                        </button>
                        
                        {/* View Feedback Button with Enhanced Display */}
                        <div className="relative">
                          <button
                            onClick={() => openRepliesModal(post.id, post.title, 'public')}
                            className="flex items-center gap-1 text-sm text-gray-400 hover:text-blue-400 transition-colors duration-200 px-2 py-1 rounded-md hover:bg-blue-500/10"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                            <span className="whitespace-nowrap">
                              {post.public_replies_count !== undefined ? (
                                <div className="flex items-center gap-1">
                                  <span className="text-green-400 font-medium">{post.public_replies_count}</span>
                                  <span className="text-gray-500">public</span>
                                  {post.is_post_author && post.private_replies_count > 0 && (
                                    <>
                                      <span className="text-gray-500">•</span>
                                      <span className="text-purple-400 font-medium">{post.private_replies_count}</span>
                                      <span className="text-gray-500">private</span>
                                    </>
                                  )}
                                </div>
                              ) : (
                                `${post.replies_count || 0} replies`
                              )}
                            </span>
                          </button>
                          
                          {/* Private Feedback Badge for Post Authors */}
                          {post.is_post_author && post.private_replies_count > 0 && (
                            <div className="absolute -top-1 -right-1 w-3 h-3 bg-purple-500 rounded-full flex items-center justify-center">
                              <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                              </svg>
                            </div>
                          )}
                        </div>
                        
                        {/* Like Button */}
                        <button
                          onClick={() => handleLikeClick(post.id)}
                          className={`flex items-center gap-1 text-sm transition-colors duration-200 px-2 py-1 rounded-md ${
                            post.user_liked 
                              ? 'text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20' 
                              : 'text-gray-400 hover:text-red-400 hover:bg-red-500/10'
                          }`}
                        >
                          <ThumbsUp size={14} className={post.user_liked ? 'fill-current' : ''} />
                          {post.likes_count || 0}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-500 mb-4">
                {info.icon}
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">No posts yet</h3>
              <p className="text-gray-400">Be the first to start a discussion in this category!</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Format number for display
  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  // Format duration
  const formatDuration = (duration) => {
    return duration; // Already formatted as MM:SS
  };

  // Handle video click - redirect to YouTube
  const handleVideoClick = (video) => {
    // Open YouTube video in new tab
    const youtubeUrl = video.youtube_url || video.youtubeUrl || `https://www.youtube.com/watch?v=${video.youtube_id}`;
    window.open(youtubeUrl, '_blank');
    
    // Track view count (optional - fire and forget)
    if (video.id) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/videos/${video.id}/view`, {
        method: 'POST'
      }).catch(err => console.log('Failed to track view:', err));
    }
  };

  const renderContent = () => {
    const content = mockContent[selectedCategory];
    
    switch (selectedCategory) {
      case 'videos':
        return (
          <div className="video-board-container">
            {/* Video Board Header with Refresh Button */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white mb-1">Video Board</h3>
                <p className="text-sm text-gray-400">Discover the latest production tutorials and insights</p>
              </div>
              
              {/* Refresh Videos Button - Moved Outside Search Panel */}
              <button
                onClick={() => {
                  loadVideos(true); // Force refresh
                  loadVideoCategories();
                }}
                disabled={loading}
                className="bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500/50 text-purple-300 hover:text-white px-4 py-2 rounded-lg text-sm transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Refresh videos from YouTube API"
              >
                <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {loading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>

            {/* Search and Filter Controls */}
            <div className="video-controls mb-6">
              <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
                {/* Search Bar */}
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type="text"
                    placeholder="Search videos, topics, or tags..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-gray-700/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-purple-500/50 focus:bg-gray-800/70 transition-all duration-300"
                  />
                </div>
                
                {/* Filter and Sort Controls */}
                <div className="flex gap-3">
                  {/* Category Filter */}
                  <div className="relative">
                    <select
                      value={selectedVideoCategory}
                      onChange={(e) => setSelectedVideoCategory(e.target.value)}
                      className="appearance-none bg-gray-800/50 border border-gray-700/50 rounded-lg px-4 py-2 pr-8 text-white text-sm focus:outline-none focus:border-purple-500/50 cursor-pointer"
                    >
                      {videoCategories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name} ({category.count})
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
                  </div>
                  
                  {/* Sort Dropdown */}
                  <div className="relative">
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="appearance-none bg-gray-800/50 border border-gray-700/50 rounded-lg px-4 py-2 pr-8 text-white text-sm focus:outline-none focus:border-purple-500/50 cursor-pointer"
                    >
                      <option value="trending">🔥 Trending</option>
                      <option value="newest">📅 Newest</option>
                      <option value="most-viewed">👁️ Most Viewed</option>
                      <option value="most-liked">👍 Most Liked</option>
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
                  </div>
                </div>
              </div>
              
              {/* Results Info */}
              <div className="mt-3 flex items-center justify-between text-sm text-gray-400">
                <div>
                  {searchTerm ? (
                    `Found ${filteredVideos.length} video${filteredVideos.length !== 1 ? 's' : ''} for "${searchTerm}"`
                  ) : (
                    `Showing ${filteredVideos.length} videos from YouTube API`
                  )}
                </div>
                <div className="text-xs">
                  Last updated: {new Date().toLocaleTimeString()}
                </div>
              </div>
            </div>

            {/* Video Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="flex flex-col items-center gap-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                  <p className="text-gray-400">Loading awesome content...</p>
                </div>
              </div>
            ) : filteredVideos.length > 0 ? (
              <div className="video-masonry-grid">
                {filteredVideos.map((video) => (
                  <div
                    key={video.id}
                    onClick={() => handleVideoClick(video)}
                    className="video-card bg-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-xl overflow-hidden hover:border-purple-500/50 hover:bg-gray-800/50 transition-all duration-300 cursor-pointer transform hover:scale-[1.02] hover:shadow-lg hover:shadow-purple-500/10"
                  >
                    {/* Video Thumbnail */}
                    <div className="relative group">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/480x270/1a1a2e/8b5cf6?text=Video+Thumbnail';
                        }}
                      />
                      
                      {/* Play Overlay */}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                        <div className="bg-purple-600 rounded-full p-3 transform scale-90 group-hover:scale-100 transition-transform duration-300">
                          <Play className="text-white" size={24} />
                        </div>
                      </div>
                      
                      {/* Duration Badge */}
                      {video.duration && video.duration !== '0:00' && (
                        <div className="absolute bottom-2 right-2 bg-black/70 backdrop-blur-sm text-white text-xs px-2 py-1 rounded">
                          {formatDuration(video.duration)}
                        </div>
                      )}
                      
                      {/* Trending Badge */}
                      {video.trending > 90 && (
                        <div className="absolute top-2 left-2 bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                          🔥 Trending
                        </div>
                      )}
                    </div>

                    {/* Video Info */}
                    <div className="p-4">
                      <h4 className="font-semibold text-white text-sm mb-2 line-clamp-2 leading-tight">
                        {video.title}
                      </h4>
                      
                      <p className="text-gray-300 text-xs mb-3 line-clamp-2 leading-relaxed">
                        {video.description}
                      </p>
                      
                      {/* Stats Row */}
                      <div className="flex items-center justify-between text-xs text-gray-400 mb-3">
                        <div className="flex items-center gap-3">
                          <span className="flex items-center gap-1">
                            <Eye size={12} />
                            {formatNumber(video.views)}
                          </span>
                          <span className="flex items-center gap-1">
                            <ThumbsUp size={12} />
                            {formatNumber(video.likes)}
                          </span>
                        </div>
                        <span className="bg-purple-600/20 text-purple-300 px-2 py-1 rounded text-xs">
                          {video.level}
                        </span>
                      </div>
                      
                      {/* Tags */}
                      <div className="flex flex-wrap gap-1">
                        {video.tags.slice(0, 2).map((tag, index) => (
                          <span key={index} className="bg-gray-700/50 text-gray-300 px-2 py-1 rounded text-xs">
                            #{tag}
                          </span>
                        ))}
                        {video.tags.length > 2 && (
                          <span className="text-gray-500 text-xs">+{video.tags.length - 2}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="text-gray-500 mb-4">
                  <Youtube size={48} />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">No Videos Found</h3>
                <p className="text-gray-400 text-center max-w-md">
                  {searchTerm ? `No videos match "${searchTerm}". Try different keywords.` : 'No videos available in this category.'}
                </p>
              </div>
            )}
          </div>
        );

      case 'forum':
        return renderForumContent();
      case 'samples':
        return (
          <div data-testid="sample-finder" style={{ padding: '0.5rem' }}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#fff', marginBottom: '4px' }}>Track DNA</h3>
            <p style={{ fontSize: '0.8rem', color: '#888', marginBottom: '1rem' }}>Break down any track — credits, remixes, samples, genre tags & release history</p>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '1rem' }}>
              <input
                data-testid="sample-finder-title"
                value={sampleQuery}
                onChange={(e) => setSampleQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && document.querySelector('[data-testid="sample-finder-search-btn"]')?.click()}
                placeholder="Track title (optional if artist filled)..."
                style={{ flex: 2, padding: '10px 14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: '#fff', fontSize: '13px', outline: 'none' }}
              />
              <input
                data-testid="sample-finder-artist"
                value={sampleArtist}
                onChange={(e) => setSampleArtist(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && document.querySelector('[data-testid="sample-finder-search-btn"]')?.click()}
                placeholder="Artist (optional if title filled)..."
                style={{ flex: 1, padding: '10px 14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: '#fff', fontSize: '13px', outline: 'none' }}
              />
              <button
                data-testid="sample-finder-search-btn"
                onClick={async () => {
                  const hasTitle = sampleQuery.trim().length > 0;
                  const hasArtist = sampleArtist.trim().length > 0;
                  if (!hasTitle && !hasArtist) return;
                  setSampleLoading(true); setSampleResult(null); setArtistRecordings(null);
                  try {
                    const BURL = process.env.REACT_APP_BACKEND_URL;
                    const params = new URLSearchParams();
                    if (hasTitle) params.append('title', sampleQuery.trim());
                    if (hasArtist) params.append('artist', sampleArtist.trim());
                    const res = await fetch(`${BURL}/api/musicbrainz/recording/search?${params}`);
                    const json = await res.json();
                    if (json.success) {
                      if (json.mode === 'artist_browse') {
                        // Artist-only mode: show recording list
                        setArtistRecordings(json.data);
                      } else {
                        // Recording found directly — fetch full details
                        const d = { ...json.data };
                        if (d.mbid) {
                          const [relRes, detRes] = await Promise.all([
                            fetch(`${BURL}/api/musicbrainz/recording/${d.mbid}/relationships`),
                            fetch(`${BURL}/api/musicbrainz/recording/${d.mbid}`),
                          ]);
                          const relJson = await relRes.json();
                          const detJson = await detRes.json();
                          if (relJson.success) d.relationships = relJson.data;
                          if (detJson.success) d.detail = detJson.data;
                        }
                        setSampleResult(d);
                      }
                    } else {
                      setSampleResult({ notFound: true, message: json.message });
                    }
                  } catch (e) { console.error(e); }
                  setSampleLoading(false);
                }}
                disabled={sampleLoading || (!sampleQuery.trim() && !sampleArtist.trim())}
                style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #fbbf24, #f59e0b)', border: 'none', borderRadius: '8px', color: '#000', fontWeight: 700, fontSize: '13px', cursor: 'pointer', opacity: sampleLoading ? 0.5 : 1 }}
              >
                {sampleLoading ? '...' : 'Search'}
              </button>
            </div>

            {/* Search mode hint */}
            <div style={{ fontSize: '10px', color: '#555', marginBottom: '12px', display: 'flex', gap: '12px' }}>
              {sampleQuery.trim() && sampleArtist.trim() && <span style={{ color: '#fbbf24' }}>Searching: title + artist</span>}
              {sampleQuery.trim() && !sampleArtist.trim() && <span style={{ color: '#00d4ff' }}>Searching: title across all artists</span>}
              {!sampleQuery.trim() && sampleArtist.trim() && <span style={{ color: '#ff66ff' }}>Browsing: all recordings by artist</span>}
            </div>

            {/* Artist-Only Mode: Show recording list to pick from */}
            {artistRecordings && !sampleResult && (
              <div data-testid="artist-recordings-list" style={{
                background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,0,255,0.2)',
                borderRadius: '12px', padding: '14px', maxHeight: '400px', overflowY: 'auto',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <span style={{ fontSize: '10px', color: '#ff66ff', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>
                    {artistRecordings.artist?.name} — {artistRecordings.recordings?.length || 0} recordings
                  </span>
                  <button
                    onClick={() => { setArtistRecordings(null); }}
                    style={{ marginLeft: 'auto', background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', padding: '2px 8px', color: '#888', fontSize: '10px', cursor: 'pointer' }}
                  >Clear</button>
                </div>
                <div style={{ fontSize: '10px', color: '#666', marginBottom: '10px' }}>Select a track to view credits, samples & remix info</div>
                {artistRecordings.recordings?.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '1rem', color: '#666' }}>No recordings found for this artist</div>
                )}
                {artistRecordings.recordings?.map((rec, i) => (
                  <div
                    key={rec.mbid || i}
                    data-testid={`artist-recording-${i}`}
                    onClick={async () => {
                      if (!rec.mbid || selectedRecordingMbid === rec.mbid) return;
                      setSelectedRecordingMbid(rec.mbid);
                      setSampleLoading(true);
                      try {
                        const BURL = process.env.REACT_APP_BACKEND_URL;
                        const d = { ...rec };
                        const [relRes, detRes] = await Promise.all([
                          fetch(`${BURL}/api/musicbrainz/recording/${rec.mbid}/relationships`),
                          fetch(`${BURL}/api/musicbrainz/recording/${rec.mbid}`),
                        ]);
                        const relJson = await relRes.json();
                        const detJson = await detRes.json();
                        if (relJson.success) d.relationships = relJson.data;
                        if (detJson.success) d.detail = detJson.data;
                        setSampleResult(d);
                        setArtistRecordings(null);
                      } catch (e) { console.error(e); }
                      setSampleLoading(false);
                      setSelectedRecordingMbid(null);
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '8px 10px', marginBottom: '4px',
                      background: selectedRecordingMbid === rec.mbid ? 'rgba(255,0,255,0.1)' : 'rgba(255,255,255,0.02)',
                      border: `1px solid ${selectedRecordingMbid === rec.mbid ? 'rgba(255,0,255,0.3)' : 'rgba(255,255,255,0.04)'}`,
                      borderRadius: '8px', cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => { if (selectedRecordingMbid !== rec.mbid) e.currentTarget.style.background = 'rgba(255,0,255,0.06)'; }}
                    onMouseLeave={(e) => { if (selectedRecordingMbid !== rec.mbid) e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}
                  >
                    <span style={{ fontSize: '10px', color: '#555', fontWeight: 600, width: '20px', textAlign: 'right', flexShrink: 0 }}>{i + 1}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {rec.title}
                      </div>
                      {rec.artist_display && rec.artist_display !== artistRecordings.artist?.name && (
                        <div style={{ fontSize: '10px', color: '#888', marginTop: '1px' }}>{rec.artist_display}</div>
                      )}
                    </div>
                    {rec.length_ms && (
                      <span style={{ fontSize: '10px', color: '#555', flexShrink: 0 }}>
                        {Math.floor(rec.length_ms / 60000)}:{String(Math.floor((rec.length_ms % 60000) / 1000)).padStart(2, '0')}
                      </span>
                    )}
                    {rec.tags?.length > 0 && (
                      <span style={{ padding: '1px 6px', fontSize: '9px', background: 'rgba(255,0,255,0.1)', border: '1px solid rgba(255,0,255,0.2)', borderRadius: '6px', color: '#ff66ff', flexShrink: 0 }}>
                        {rec.tags[0].name}
                      </span>
                    )}
                    {selectedRecordingMbid === rec.mbid ? (
                      <span style={{ fontSize: '10px', color: '#ff66ff' }}>Loading...</span>
                    ) : (
                      <span style={{ fontSize: '18px', color: '#555', flexShrink: 0 }}>›</span>
                    )}
                  </div>
                ))}
                <div style={{ textAlign: 'center', marginTop: '8px' }}>
                  <span style={{ fontSize: '9px', color: '#444', fontStyle: 'italic' }}>Powered by MusicBrainz</span>
                </div>
              </div>
            )}

            {sampleResult?.notFound && (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                {sampleResult.message || "No results found. Try a different track title or artist name."}
                {!sampleQuery.trim() && sampleArtist.trim() && (
                  <div style={{ marginTop: '8px', fontSize: '12px', color: '#888' }}>Try adding a track title to narrow results</div>
                )}
              </div>
            )}

            {sampleResult && !sampleResult.notFound && (
              <div data-testid="sample-finder-results" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(251,191,36,0.15)', borderRadius: '12px', padding: '16px' }}>
                {/* Back button when coming from artist browse */}
                {sampleArtist.trim() && !sampleQuery.trim() && (
                  <button
                    data-testid="sample-finder-back-btn"
                    onClick={() => {
                      setSampleResult(null);
                      // Re-trigger artist browse
                      document.querySelector('[data-testid="sample-finder-search-btn"]')?.click();
                    }}
                    style={{
                      background: 'rgba(255,0,255,0.1)', border: '1px solid rgba(255,0,255,0.25)',
                      borderRadius: '6px', padding: '4px 12px', color: '#ff66ff', fontSize: '11px',
                      cursor: 'pointer', marginBottom: '10px', fontWeight: 600,
                    }}
                  >← Back to artist recordings</button>
                )}
                <div style={{ marginBottom: '12px' }}>
                  <h4 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#fff', margin: 0 }}>{sampleResult.title}</h4>
                  <p style={{ fontSize: '0.9rem', color: '#fbbf24', margin: '2px 0 0', fontWeight: 600 }}>{sampleResult.artist_display}</p>
                  {sampleResult.length_ms && (
                    <span style={{ fontSize: '11px', color: '#666' }}>{Math.floor(sampleResult.length_ms / 60000)}:{String(Math.floor((sampleResult.length_ms % 60000) / 1000)).padStart(2, '0')}</span>
                  )}
                </div>

                {/* Full Credits */}
                {(sampleResult.detail?.artist_credits || sampleResult.artist_credits)?.length > 0 && (
                  <div style={{ marginBottom: '12px' }}>
                    <span style={{ fontSize: '11px', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Production Credits</span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                      {(sampleResult.detail?.artist_credits || sampleResult.artist_credits).map((ac, i) => (
                        <span key={i} style={{ padding: '4px 10px', background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.25)', borderRadius: '8px', fontSize: '12px', color: '#fbbf24', fontWeight: 600 }}>
                          {ac.name}{ac.join_phrase && <span style={{ color: '#666' }}>{ac.join_phrase}</span>}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* ISRCs */}
                {sampleResult.detail?.isrcs?.length > 0 && (
                  <div style={{ marginBottom: '10px' }}>
                    <span style={{ fontSize: '11px', color: '#888', fontWeight: 600, textTransform: 'uppercase' }}>ISRC</span>
                    <div style={{ marginTop: '4px' }}>
                      {sampleResult.detail.isrcs.slice(0, 2).map((isrc, i) => (
                        <span key={i} style={{ fontSize: '11px', color: '#aaa', fontFamily: 'monospace', marginRight: '8px' }}>{isrc}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Genre Tags */}
                {(sampleResult.detail?.tags || sampleResult.tags)?.length > 0 && (
                  <div style={{ marginBottom: '12px' }}>
                    <span style={{ fontSize: '11px', color: '#888', fontWeight: 600, textTransform: 'uppercase' }}>Genre Tags</span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
                      {(sampleResult.detail?.tags || sampleResult.tags).slice(0, 10).map((tag, i) => (
                        <span key={i} style={{ padding: '2px 8px', background: 'rgba(255,0,255,0.08)', border: '1px solid rgba(255,0,255,0.15)', borderRadius: '8px', fontSize: '11px', color: '#ff66ff' }}>{tag.name}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* SAMPLE RELATIONSHIPS - The Star Feature */}
                {sampleResult.relationships?.samples?.length > 0 && (
                  <div data-testid="sample-finder-samples" style={{ marginBottom: '12px', padding: '12px', background: 'rgba(0,255,136,0.05)', border: '1px solid rgba(0,255,136,0.2)', borderRadius: '10px' }}>
                    <span style={{ fontSize: '12px', color: '#00ff88', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Samples Used</span>
                    {sampleResult.relationships.samples.map((s, i) => (
                      <div key={i} style={{ fontSize: '12px', color: '#00ff88', padding: '4px 0', marginTop: '4px' }}>
                        Samples "{s.target_title}" {s.target_artist && `by ${s.target_artist}`}
                      </div>
                    ))}
                  </div>
                )}

                {/* REMIX RELATIONSHIPS */}
                {sampleResult.relationships?.remixes?.length > 0 && (
                  <div style={{ marginBottom: '12px', padding: '12px', background: 'rgba(255,165,0,0.05)', border: '1px solid rgba(255,165,0,0.2)', borderRadius: '10px' }}>
                    <span style={{ fontSize: '12px', color: '#ffa500', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Remix History</span>
                    {sampleResult.relationships.remixes.map((r, i) => (
                      <div key={i} style={{ fontSize: '12px', color: '#ffa500', padding: '3px 0', marginTop: '4px' }}>
                        {r.direction === 'backward' ? `Remix of "${r.target_title}" by ${r.target_artist}` : `Remixed as "${r.target_title}" by ${r.target_artist}`}
                      </div>
                    ))}
                  </div>
                )}

                {/* COVER RELATIONSHIPS */}
                {sampleResult.relationships?.covers?.length > 0 && (
                  <div style={{ marginBottom: '12px', padding: '12px', background: 'rgba(0,212,255,0.05)', border: '1px solid rgba(0,212,255,0.2)', borderRadius: '10px' }}>
                    <span style={{ fontSize: '12px', color: '#00d4ff', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Covers</span>
                    {sampleResult.relationships.covers.slice(0, 5).map((c, i) => (
                      <div key={i} style={{ fontSize: '12px', color: '#00d4ff', padding: '3px 0', marginTop: '4px' }}>
                        {c.direction === 'backward' ? `Cover of "${c.target_title}"` : `Covered by ${c.target_artist}`}
                      </div>
                    ))}
                  </div>
                )}

                {/* Releases */}
                {(sampleResult.detail?.releases || sampleResult.releases)?.length > 0 && (
                  <div style={{ marginBottom: '10px' }}>
                    <span style={{ fontSize: '11px', color: '#888', fontWeight: 600, textTransform: 'uppercase' }}>Released On</span>
                    <div style={{ marginTop: '4px' }}>
                      {(sampleResult.detail?.releases || sampleResult.releases).slice(0, 5).map((rel, i) => (
                        <div key={i} style={{ fontSize: '11px', color: '#aaa', padding: '2px 0' }}>
                          <span style={{ color: '#b06aff' }}>{rel.title}</span>
                          {rel.date && <span style={{ color: '#555', marginLeft: '6px' }}>({rel.date})</span>}
                          {rel.release_group_type && <span style={{ color: '#555', marginLeft: '4px' }}>[{rel.release_group_type}]</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ textAlign: 'right', marginTop: '8px', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '6px' }}>
                  <span style={{ fontSize: '9px', color: '#444', fontStyle: 'italic' }}>Powered by MusicBrainz</span>
                </div>
              </div>
            )}
          </div>
        );
      case 'tips':
        return (
          <div className="space-y-4">
            {content.map((tip) => (
              <div key={tip.id} className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6 hover:border-purple-500/30 transition-all duration-300">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="font-semibold text-white text-lg mb-2">{tip.title}</h4>
                    <p className="text-gray-300 text-sm mb-3">{tip.description}</p>
                    <div className="flex items-center text-xs text-gray-400 space-x-4">
                      <span>By {tip.author}</span>
                      <span>{tip.date}</span>
                      <span className="capitalize bg-purple-600/20 px-2 py-1 rounded">{tip.type}</span>
                    </div>
                  </div>
                  <ExternalLink className="text-purple-400 cursor-pointer" size={20} />
                </div>
                <div className="flex flex-wrap gap-2">
                  {tip.tags.map((tag, index) => (
                    <span key={index} className="bg-blue-600/20 text-blue-300 px-2 py-1 rounded text-xs">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        );
        
      case 'demos':
        // Use the DemoDropsTab component with search/filter
        return <DemoDropsTab />;
        
      case 'labels':
        // Use the new ConnectTab with 3 panels (Labels, Collabs, Vocalists)
        return <ConnectTab />;
        
      case 'news':
        return (
          <div className="space-y-4">
            {content.map((article) => (
              <div key={article.id} className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6 hover:border-red-500/30 transition-all duration-300">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="font-semibold text-white text-lg mb-2">{article.title}</h4>
                    <p className="text-gray-300 text-sm mb-3">{article.summary}</p>
                    <div className="flex items-center text-xs text-gray-400 space-x-4">
                      <span>{article.date}</span>
                      <span>Source: {article.source}</span>
                    </div>
                  </div>
                  <ExternalLink className="text-red-400 cursor-pointer" size={20} />
                </div>
              </div>
            ))}
          </div>
        );
        
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 backdrop-blur-sm flex items-center justify-center z-40 p-4">
      {/* Modal with animated gradient border */}
      <div 
        className="producer-corner-modal"
        style={{
          width: '100%',
          maxWidth: '1200px',
          height: '700px',
          borderRadius: '20px',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
          padding: '3px',
          background: 'linear-gradient(90deg, #9333ea, #fbbf24, #9333ea, #fbbf24)',
          backgroundSize: '300% 300%',
          animation: 'producerModalBorderFlow 4s ease-in-out infinite'
        }}
      >
        <style>{`
          @keyframes producerModalBorderFlow {
            0%, 100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }
        `}</style>
        
        {/* Inner container */}
        <div className="flex flex-col flex-1 overflow-hidden" style={{
          background: 'linear-gradient(135deg, rgba(15, 10, 30, 0.99), rgba(25, 15, 45, 0.99))',
          borderRadius: '17px'
        }}>
          {/* Header */}
          <div className="relative p-6 border-b" style={{
            background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.3), rgba(251, 191, 36, 0.15))',
            borderColor: 'rgba(147, 51, 234, 0.3)'
          }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg" style={{
                    background: 'linear-gradient(135deg, #9333ea, #fbbf24)'
                  }}>
                    <Mic className="text-white" size={24} />
                  </div>
                  <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center" style={{
                    background: 'linear-gradient(135deg, #fbbf24, #f59e0b)'
                  }}>
                    <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                  </div>
                </div>
                <div>
                  <h2 className="text-2xl font-bold" style={{
                    background: 'linear-gradient(135deg, #fbbf24, #9333ea)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>
                    Producers Corner
                  </h2>
                  <p style={{ color: 'rgba(251, 191, 36, 0.8)', fontSize: '0.875rem' }}>Tips, demos, labels & industry insights</p>
                </div>
              </div>
              
              <button 
                onClick={onClose}
                className="p-2 rounded-full transition-all duration-200"
                style={{
                  color: '#fbbf24',
                  border: '2px solid rgba(147, 51, 234, 0.5)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(147, 51, 234, 0.2)';
                  e.currentTarget.style.borderColor = '#9333ea';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = 'rgba(147, 51, 234, 0.5)';
                }}
              >
                <X size={24} />
              </button>
            </div>
            
            {/* Animated background elements */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
              <div className="absolute top-2 left-10 w-2 h-2 rounded-full animate-ping" style={{ background: 'rgba(251, 191, 36, 0.3)' }}></div>
              <div className="absolute top-8 right-20 w-1 h-1 rounded-full animate-bounce" style={{ background: 'rgba(147, 51, 234, 0.4)' }}></div>
              <div className="absolute bottom-4 left-1/3 w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: 'rgba(251, 191, 36, 0.2)' }}></div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
            {/* Sidebar */}
            <div className="w-full md:w-80 shrink-0 max-h-[40vh] md:max-h-none border-b md:border-b-0 md:border-r flex flex-col p-6" style={{
              background: 'linear-gradient(180deg, rgba(20, 10, 35, 0.95), rgba(15, 8, 25, 0.95))',
              borderColor: 'rgba(147, 51, 234, 0.2)',
              overflowY: 'auto',
            }}>
              <h3 className="text-lg font-bold text-white mb-6 flex items-center" style={{ flexShrink: 0 }}>
                <Zap className="mr-2" size={20} style={{ color: '#fbbf24' }} />
                Categories
              </h3>
              <div className="space-y-3" style={{ overflowY: 'auto', flex: 1, paddingBottom: '8px' }}>
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`w-full text-left p-4 rounded-xl transition-all duration-300 transform hover:scale-105`}
                    style={{
                      background: selectedCategory === category.id 
                        ? 'linear-gradient(135deg, rgba(147, 51, 234, 0.6), rgba(251, 191, 36, 0.3))'
                        : 'rgba(30, 20, 50, 0.5)',
                      border: selectedCategory === category.id 
                        ? '1px solid rgba(147, 51, 234, 0.5)'
                        : '1px solid rgba(147, 51, 234, 0.2)',
                      boxShadow: selectedCategory === category.id 
                        ? '0 4px 20px rgba(147, 51, 234, 0.3)'
                        : 'none'
                    }}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="p-2 rounded-lg" style={{
                        background: selectedCategory === category.id 
                          ? 'rgba(251, 191, 36, 0.2)' 
                          : 'rgba(147, 51, 234, 0.2)'
                      }}>
                        {category.icon}
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-sm" style={{
                          color: selectedCategory === category.id ? '#fbbf24' : '#fff'
                        }}>
                          {category.name}
                        </div>
                        <div className="text-xs" style={{
                          color: selectedCategory === category.id ? 'rgba(251, 191, 36, 0.7)' : 'rgba(255, 255, 255, 0.5)'
                        }}>
                          {category.description || (category.count ? `${category.count} items` : '')}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 p-6 overflow-y-auto custom-scrollbar" style={{
              background: 'linear-gradient(180deg, rgba(15, 8, 25, 0.95), rgba(20, 12, 35, 0.95))'
            }}>
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-white mb-2">
                  {categories.find(cat => cat.id === selectedCategory)?.name}
                </h3>
                <p style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                  Discover the latest {selectedCategory === 'tips' ? 'production techniques' : 
                selectedCategory === 'demos' ? 'submission opportunities and contests' :
                selectedCategory === 'labels' ? 'labels, collaborators & vocalists' : 'industry updates'}
              </p>
            </div>
            
            {renderContent()}
          </div>
        </div>
      </div>
      </div>
      
      {/* Phase 5: Notifications Dropdown */}
      {renderNotificationsDropdown()}
      
      {/* Reply Modal */}
      {renderReplyModal()}
      
      {/* Replies View Modal */}
      {renderRepliesViewModal()}
      
      {/* Password Verification Modal */}
      {renderPasswordVerificationModal()}
      
      {/* My Tracks Modal */}
      {renderMyTracksModal()}
      
      {/* Audio Warning Modal */}
      {renderAudioWarningModal()}
      
      {/* New Post Modal */}
      {renderNewPostModal()}
      
      {/* Emote Picker Modal */}
      <EmotePicker 
        isOpen={showEmotePicker}
        onClose={() => setShowEmotePicker(false)}
        onEmoteSelect={(emoteCode) => {
          if (emoteTargetField === 'newPost') {
            setNewPostContent(prev => prev + emoteCode);
          } else if (emoteTargetField === 'reply') {
            setReplyContent(prev => prev + emoteCode);
          }
        }}
      />
    </div>
  );
};

export default ProducersCorner;